import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import {
  Itinerary,
  ItineraryAttraction,
} from '../../trip-planner/models/itinerary';
import { ButtonTriggerEvent } from '../../../common/components/button/button.component';
import { VisitorEvent } from '../models/visitor.event';
import { VisitorPlace } from '../models/visitor.place';
import { ScriptService } from '../../../common/services/load.script/load.script.service';
import { ConfigService } from '../../../common/services/config/config.service';
import { ImageService } from '../../../common/services/image/image.service';
import { VisitorService } from './visitor.service';
import {
  clone,
  isNullOrUndefined,
  upperFirstLetter,
} from '../../../common/utils/object.extensions';
import { GoogleMapService, TravelMode } from './google.map.service';
import { EventTimeService } from '../../event-calendar/services/event.time.service';
import {
  buildCalendarMonthPdfModel,
  CalendarMonthPdfDay,
  CalendarMonthPdfEntry,
  CalendarMonthPdfModel,
  CalendarMonthPdfWeek,
} from '../../event-calendar/utils/calendar.month.pdf.mapper';
import { removeHtmlTags } from '../../../common/utils/string.extensions';
import { hexToRgb } from '../../../common/utils/color.utils';
import {
  TripAttraction,
  TripDay,
  UserTrip,
} from '../models/vititor.trip.storage.command';
import { TripPlannerService } from '../../trip-planner/services/trip.planner.service';

// Declare global types for TypeScript
declare global {
  interface Window {
    jsPDF: any;
    jspdf: any;
    html2canvas: any;
  }
}

const MAP_PIN_HEIGHT_PX = 44;

@Injectable({
  providedIn: 'root',
})
export class ExportPDFService {
  public doc: any;
  public pdfImageBase64: Array<PDFImgDataObject>;
  public defaultTopImages: Partial<Record<PDFType, string>> = {};
  public defaultTitleText: Record<
    'event' | 'place' | 'tour' | 'trip',
    { text: string; x: number; fontSize?: number }
  > = {
    event: { text: "Events You Can't Miss", x: 6 },
    place: { text: 'Places to Visit', x: 18 },
    tour: { text: 'Visit Our Suggested Itineraries', x: 3, fontSize: 16 },
    trip: { text: 'Welcome to My Trip', x: 10 },
  };
  public textDefaultColor = '#404040';
  public bannerHeight = 28;
  public pageNumber = 1;
  public y = 0;
  public maxY = 0;
  public itemInfoToPrint = {
    event: ['image', 'name', 'time', 'organizer', 'address', 'more'],
    place: ['image', 'name', 'category', 'rating', 'address', 'more'],
  };
  public errorFallbackImage =
    'https://iti-images.s3.amazonaws.com/events/6ae81b06-ce41-9f93-af1b-cb4f60d749a8.png';
  public command: ExportPDFCommandBase;
  private renderer: Renderer2;
  private librariesLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(
    public visitorService: VisitorService,
    public imageService: ImageService,
    public timeService: EventTimeService,
    public tripService: TripPlannerService,
    public googleMapService: GoogleMapService,
    public scriptService: ScriptService,
    private rendererFactory: RendererFactory2
  ) {
    this.pdfImageBase64 = [];
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  /**
   * IMPROVED APPROACH: CDN-first library loading with NPM fallback for better space efficiency
   */
  private async loadPDFLibraries(): Promise<void> {
    if (this.librariesLoaded) {
      console.log('✅ [ExportPDFService] Libraries already loaded');
      return;
    }

    console.log('🚀 [ExportPDFService] Starting PDF library loading...');

    // Add diagnostic information for iframe debugging
    this.logIframeDiagnostics();

    await this.performLibraryLoad();
  }

  /**
   * Log diagnostic information to help debug iframe script blocking issues
   */
  private logIframeDiagnostics(): void {
    console.log('🔍 [ExportPDFService] Iframe Diagnostics:');
    console.log('  - Current origin:', window.location.origin);
    console.log('  - Parent origin:', window.parent !== window ? 'Different origin' : 'Same origin');
    console.log('  - Document ready state:', document.readyState);
    console.log('  - Script injection allowed:', this.canInjectScripts());
    console.log('  - CSP restrictions:', this.checkCSPRestrictions());
    console.log('  - Sandbox restrictions:', this.checkSandboxRestrictions());
  }

  /**
   * Check if script injection is allowed in current context
   */
  private canInjectScripts(): boolean {
    try {
      // Try to create and inject a test script
      const testScript = document.createElement('script');
      testScript.textContent = 'window._testScriptInjection = true;';
      document.head.appendChild(testScript);
      document.head.removeChild(testScript);
      return true;
    } catch (error) {
      console.warn('⚠️ [ExportPDFService] Script injection blocked:', error);
      return false;
    }
  }

  /**
   * Check for Content Security Policy restrictions
   */
  private checkCSPRestrictions(): string {
    const metaCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (metaCSP) {
      return `CSP meta tag found: ${metaCSP.getAttribute('content')}`;
    }

    // Check for CSP header (this won't work in iframe context, but good for debugging)
    return 'No CSP meta tag found';
  }

  /**
   * Check for iframe sandbox restrictions
   */
  private checkSandboxRestrictions(): string {
    try {
      // Try to access parent window properties
      if (window.parent !== window) {
        const iframe = Array.from(window.parent.document.querySelectorAll('iframe')).find(
          iframe => iframe.contentWindow === window
        );
        if (iframe) {
          const sandbox = iframe.getAttribute('sandbox');
          return sandbox ? `Sandbox restrictions: ${sandbox}` : 'No sandbox restrictions';
        }
      }
      return 'Not in iframe context';
    } catch (error) {
      return `Sandbox check failed: ${error.message}`;
    }
  }

  private async performLibraryLoad(): Promise<void> {
    console.log('🚀 [ExportPDFService] Starting library loading...');

    try {
      // Method 1: Try CDN first (preferred for space efficiency)
      await this.loadViaSingleCDN();
      console.log('✅ [ExportPDFService] CDN libraries loaded successfully');
    } catch (cdnError) {
      console.log('📡 [ExportPDFService] CDN loading failed, trying NPM packages as fallback...');

      try {
        // Method 2: NPM packages as fallback (if available)
        await this.loadViaNPMFallback();
        console.log('✅ [ExportPDFService] NPM packages loaded successfully as fallback');
      } catch (npmError) {
        console.error('❌ [ExportPDFService] Both CDN and NPM loading failed:', { cdnError, npmError });

        // Provide detailed error information and solutions
        const errorMessage = this.getIframeBlockingSolutions(cdnError);
        throw new Error(errorMessage);
      }
    }

    this.librariesLoaded = true;
    console.log('🎉 [ExportPDFService] Libraries loaded successfully');
  }

  /**
   * Method 1: Single CDN Loading (Simplified and Reliable)
   */
  private async loadViaSingleCDN(): Promise<void> {
    console.log('📡 [ExportPDFService] Loading libraries from CDN...');

    // Clean up any existing scripts first
    this.cleanupExistingScripts();

    // Load jsPDF first
    await this.loadSingleScript({
      url: 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js',
      libraryName: 'jsPDF',
      globalCheck: () => window.jsPDF || window.jspdf?.jsPDF,
      globalExpose: () => {
        if (window.jspdf?.jsPDF && !window.jsPDF) {
          window.jsPDF = window.jspdf.jsPDF;
        }
      },
    });

    // Load html2canvas second
    await this.loadSingleScript({
      url: 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js',
      libraryName: 'html2canvas',
      globalCheck: () => window.html2canvas,
      globalExpose: () => {
        // html2canvas usually exposes itself correctly
      },
    });

    console.log('✅ [ExportPDFService] CDN loading successful');
  }

  private async loadSingleScript(config: {
    url: string;
    libraryName: string;
    globalCheck: () => any;
    globalExpose: () => void;
  }): Promise<void> {
    console.log(
      `📦 [ExportPDFService] Loading ${config.libraryName} from: ${config.url}`
    );

    try {
      await this.injectScript(config.url, config.libraryName);

      // Wait for library to be available
      await this.waitForGlobal(config.globalCheck, 10000);

      // Apply global exposure logic
      config.globalExpose();

      // Final verification
      if (config.globalCheck()) {
        console.log(
          `✅ [ExportPDFService] ${config.libraryName} loaded successfully`
        );
        return;
      } else {
        throw new Error(`${config.libraryName} not available after loading`);
      }
    } catch (error) {
      console.error(
        `❌ [ExportPDFService] Failed to load ${config.libraryName}:`,
        error
      );
      throw error;
    }
  }

  private injectScript(url: string, libraryName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.type = 'text/javascript';
      script.async = false; // Load synchronously to maintain order
      script.crossOrigin = 'anonymous';
      script.id = `${libraryName}-script-${Date.now()}`;

      const timeout = setTimeout(() => {
        script.remove();
        reject(new Error(`Script loading timeout for ${libraryName} (15s)`));
      }, 15000);

      script.onload = () => {
        clearTimeout(timeout);
        console.log(`📥 [ExportPDFService] Script loaded successfully: ${libraryName}`);
        // Give the script more time to initialize and set up globals
        setTimeout(resolve, 200);
      };

      script.onerror = (error) => {
        clearTimeout(timeout);
        script.remove();
        console.error(`❌ [ExportPDFService] Script loading failed for ${libraryName}:`, error);
        reject(new Error(`Script loading failed for ${libraryName}: ${error}`));
      };

      // Add to head and ensure it's loaded
      document.head.appendChild(script);

      // Additional check for immediate availability
      if (libraryName === 'jsPDF' && (window.jsPDF || window.jspdf?.jsPDF)) {
        clearTimeout(timeout);
        setTimeout(resolve, 100);
      } else if (libraryName === 'html2canvas' && window.html2canvas) {
        clearTimeout(timeout);
        setTimeout(resolve, 100);
      }
    });
  }

  private waitForGlobal(
    checkFn: () => any,
    timeout: number = 5000
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const check = () => {
        if (checkFn()) {
          resolve();
        } else if (Date.now() - startTime >= timeout) {
          reject(new Error(`Global availability timeout`));
        } else {
          setTimeout(check, 50);
        }
      };

      check();
    });
  }

  /**
   * Method 3: NPM Package Fallback (if packages are installed)
   */
  private async loadViaNPMFallback(): Promise<void> {
    console.log('📦 [ExportPDFService] Trying NPM package fallback');

    try {
      // This will only work if the packages are installed via npm
      // We use dynamic imports to avoid TypeScript compilation errors
      let jsPDFModule, html2canvasModule;

      try {
        // Use dynamic import with string to avoid TypeScript compilation errors
        jsPDFModule = await (eval('import("jspdf")') as Promise<any>);
      } catch (e) {
        throw new Error('jsPDF package not installed. Run: npm install jspdf');
      }

      try {
        // Use dynamic import with string to avoid TypeScript compilation errors
        html2canvasModule = await (eval('import("html2canvas")') as Promise<any>);
      } catch (e) {
        throw new Error('html2canvas package not installed. Run: npm install html2canvas');
      }

      // Expose to global scope
      window.jsPDF = jsPDFModule.default || jsPDFModule.jsPDF || jsPDFModule;
      window.html2canvas = html2canvasModule.default || html2canvasModule;

      // Verify availability
      if (!window.jsPDF || !window.html2canvas) {
        throw new Error('NPM packages loaded but globals not set correctly');
      }

      console.log('✅ [ExportPDFService] NPM package fallback successful');
    } catch (error) {
      console.warn(
        '⚠️ [ExportPDFService] NPM package fallback failed:',
        error.message
      );
      throw error;
    }
  }

  /**
   * Method 4: Fetch and Evaluate (Last Resort)
   */
  private async loadViaFetchAndEval(): Promise<void> {
    console.log('🔄 [ExportPDFService] Trying fetch and evaluate method');

    try {
      // Load jsPDF
      const jsPDFResponse = await fetch(
        'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js'
      );
      if (!jsPDFResponse.ok)
        throw new Error(`jsPDF fetch failed: ${jsPDFResponse.status}`);

      const jsPDFCode = await jsPDFResponse.text();

      // Create a script element and execute it
      const jsPDFScript = document.createElement('script');
      jsPDFScript.textContent =
        jsPDFCode +
        `
        // Force global exposure
        if (typeof jsPDF !== 'undefined') window.jsPDF = jsPDF;
        if (typeof window.jspdf !== 'undefined' && window.jspdf.jsPDF) window.jsPDF = window.jspdf.jsPDF;
      `;
      document.head.appendChild(jsPDFScript);

      // Load html2canvas
      const html2canvasResponse = await fetch(
        'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js'
      );
      if (!html2canvasResponse.ok)
        throw new Error(
          `html2canvas fetch failed: ${html2canvasResponse.status}`
        );

      const html2canvasCode = await html2canvasResponse.text();

      const html2canvasScript = document.createElement('script');
      html2canvasScript.textContent = html2canvasCode;
      document.head.appendChild(html2canvasScript);

      // Wait for availability
      await this.waitForGlobal(() => window.jsPDF && window.html2canvas, 5000);

      console.log('✅ [ExportPDFService] Fetch and evaluate successful');
    } catch (error) {
      console.error('❌ [ExportPDFService] Fetch and evaluate failed:', error);
      throw error;
    }
  }

  private cleanupExistingScripts(): void {
    console.log('🧹 [ExportPDFService] Cleaning up existing PDF scripts...');

    // Remove any existing PDF-related scripts more thoroughly
    const selectors = [
      'script[src*="jspdf"]',
      'script[src*="html2canvas"]',
      'script[id*="jsPDF"]',
      'script[id*="html2canvas"]',
      'script[src*="cdnjs.cloudflare.com"]',
      'script[src*="cdn.jsdelivr.net"]',
      'script[src*="unpkg.com"]'
    ];

    selectors.forEach(selector => {
      const scripts = document.querySelectorAll(selector);
      scripts.forEach((script) => {
        console.log(
          '🗑️ [ExportPDFService] Removing existing script:',
          script.getAttribute('src') || script.id
        );
        script.remove();
      });
    });

    // Clear globals more thoroughly
    delete window.jsPDF;
    delete window.jspdf;
    delete window.html2canvas;

    // Force garbage collection if available
    if (window.gc) {
      try {
        window.gc();
      } catch (e) {
        // Ignore if not available
      }
    }

    console.log('✅ [ExportPDFService] Cleanup completed');
  }

  public getLibraryStatus(): string {
    const jsPDFAvailable = !!(window.jsPDF || window.jspdf?.jsPDF);
    const html2canvasAvailable = !!window.html2canvas;
    const librariesLoaded = this.librariesLoaded;

    return `
PDF Library Status:
- jsPDF: ${jsPDFAvailable ? '✅ Available' : '❌ Not Available'}
- html2canvas: ${html2canvasAvailable ? '✅ Available' : '❌ Not Available'}
- Service Status: ${librariesLoaded ? '✅ Loaded' : '❌ Not Loaded'}

Debug Information:
- Window.jsPDF: ${window.jsPDF ? 'Present' : 'Missing'}
- Window.jspdf: ${window.jspdf ? 'Present' : 'Missing'}
- Window.html2canvas: ${window.html2canvas ? 'Present' : 'Missing'}

If libraries are missing, try:
1. npm install jspdf html2canvas
2. Check browser console for errors
3. Refresh the page
    `.trim();
  }

  /**
   * UPDATED: Main export method with proper library loading
   */
  public async exportItineraryPDF(command: ExportItineraryPDFCommand) {
    try {
      console.log('🚀 [ExportPDFService] Starting PDF export...');

      // Step 1: Ensure libraries are loaded
      await this.loadPDFLibraries();

      // Step 2: Verify libraries are available
      if (!window.jsPDF || !window.html2canvas) {
        throw new Error('PDF libraries not available after loading');
      }

      this.command = command;

      // Step 3: Create PDF document
      this.createDoc(command);

      // Step 4: Continue with existing PDF generation logic
      await this.drawTopImageAndTitle(command);

      const totalItemsCount = this.calculateTotalAttractionCount(command.trips);

      let tripIndex = 0;
      for (const trip of command.trips) {
        this.drawTripTitle(trip.title.toString());
        this.drawDescription(trip.description);

        for (const day of trip.days) {
          this.addNewPageIfNeeded(command, 180);
          if (trip.days.length > 1) {
            this.y += 10;
            trip.days.length > 1 &&
              !command.isFor3DMap &&
              this.drawTripTitle(day.dateText);
          }

          const directionData = await this.drawDirectionMap(
            day,
            command.appKey
          );
          if (!directionData) {
            if (!day.attractions || !day.attractions.length) {
              this.drawNoStops();
              continue;
            } else {
              console.log('Error when getting direction map');
            }
          } else {
            this.drawTripInfo(day, directionData);
          }

          this.addNewPageIfNeeded(command, 250);

          this.pdfImageBase64 = [];
          await this.loadImageForItems(
            day.attractions.map((x) => x),
            totalItemsCount,
            0,
            command.progressCallback,
            command.logo || this.errorFallbackImage
          );

          if (!this.pdfImageBase64.length) {
            continue;
          }

          this.addNewPageIfNeeded(command, 230);
          this.drawStart();
          this.addNewPageIfNeeded(command, 250);
          this.drawItineraryAttractions(command, directionData);
          this.y < 280 && this.drawEnd();

          tripIndex !== command.trips.length - 1 &&
            this.addNewPageIfNeeded(command, 200);
          tripIndex++;

          if (command.isFor3DMap && tripIndex >= 1) {
            break;
          }
        }
      }

      this.savePDF(command);
      console.log('🎉 [ExportPDFService] PDF export completed successfully!');
    } catch (error) {
      console.error(
        '💥 [ExportPDFService] Error in exportItineraryPDF:',
        error
      );
      throw error;
    }
  }

  public async exportTripPDF(command: ExportTripPDFCommand) {
    try {
      // Ensure libraries are loaded
      await this.loadPDFLibraries();

      const totalItemsCount = this.calculateTotalTripAttractionCount(
        command.trip
      );
      command.progressCallback(3);

      await this.generateBannerImage(command, totalItemsCount);
      let imageInBanner = totalItemsCount;
      imageInBanner = totalItemsCount > 4 ? 4 : imageInBanner;

      this.command = command;
      this.createDoc(command);

      let progressStep =
        ((imageInBanner + 1) / (totalItemsCount + imageInBanner + 1)) * 100;
      command.progressCallback(progressStep);

      await this.drawTopImageAndTitle(command, 100);
      progressStep =
        ((imageInBanner + 2) / (totalItemsCount + imageInBanner + 1)) * 100;
      command.progressCallback(progressStep);

      for (const day of command.trip.days) {
        await this.loadImageForItems(
          day.attractions,
          totalItemsCount,
          imageInBanner,
          command.progressCallback,
          command.logo || this.errorFallbackImage
        );
      }
      command.progressCallback(95);

      this.drawTripTitle(command.trip.title);
      let index = 1;
      for (const day of command.trip.days) {
        this.addNewPageIfNeeded(command, 180);

        const dayTitle = `Day ${index}` === day.dateText ? `Day ${index}` : `Day ${index} (${day.dateText})`;
        const offsetX = this.getCenteredTextX(dayTitle, 210);
        this.y = this.drawText({
          text: dayTitle,
          x: offsetX + (index === 1 ? 4 : 0),
          y: this.y + 7,
        } as DrawTextCommand);

        const directionData = await this.drawDirectionMap(day, command.appKey);
        if (!directionData) {
          if (!day.attractions || !day.attractions.length) {
            this.drawNoStops();
          } else {
            console.log('Error when getting direction map');
          }
        } else {
          this.drawTripInfo(day, directionData);
        }

        this.addNewPageIfNeeded(command, 250);
        this.drawTripAttractions(day);
        index++;
      }
      command.progressCallback(100);
      document.getElementById('ig-tp-pdf-trip-banner')?.remove();

      this.savePDF(command);
    } catch (error) {
      console.error('❌ [ExportPDFService] Error in exportTripPDF:', error);
      throw error;
    }
  }

  public async exportCalendarMonthPDF(command: ExportCalendarMonthPDFCommand) {
    try {
      await this.loadPDFLibraries();

      if (!window.jsPDF) {
        throw new Error('PDF libraries not available after loading');
      }

      this.command = command;
      this.doc = new window.jsPDF('p', 'mm', 'a4');
      this.pdfImageBase64 = [];
      this.pageNumber = 1;
      this.y = 0;
      this.drawCalendarMonthPdfFooter(command);

      command.progressCallback?.(8);

      const model = buildCalendarMonthPdfModel({
        events: command.events || [],
        monthStart: command.monthStart,
        timezoneFormatter: command.timezoneFormatter,
        activeRange: command.activeRange,
      });

      command.progressCallback?.(20);

      await this.drawCalendarMonthPdfPageHeader(command, model.monthLabel, true);
      command.progressCallback?.(35);

      this.drawCalendarMonthPdfWeeks(command, model);
      command.progressCallback?.(95);

      this.savePDF(command);
    } catch (error) {
      console.error(
        '❌ [ExportPDFService] Error in exportCalendarMonthPDF:',
        error
      );
      throw error;
    }
  }

  public async exportEventListPDF(command: ExportEventPlacePDFCommand) {
    try {
      await this.loadPDFLibraries();

      if (!window.jsPDF) {
        throw new Error('PDF libraries not available after loading');
      }

      this.command = command;
      this.pdfImageBase64 = [];
      this.pageNumber = 1;
      this.y = 0;
      this.maxY = 0;
      this.createDoc(command);

      command.progressCallback?.(45);

      await this.drawEventListPdfHeader(command);
      this.drawEventListPdfPageBackground(this.y - 6);
      command.progressCallback?.(55);

      const items = (command.items || []).filter(
        (item) => !!item && item.socialName !== 'promotion'
      ) as VisitorEvent[];

      if (!items.length) {
        throw new Error('No events are available to export.');
      }

      items.forEach((item, index) => {
        this.drawEventListPdfRow(item, command);
        const progress = 55 + Math.round(((index + 1) / items.length) * 40);
        command.progressCallback?.(Math.min(progress, 95));
      });

      command.progressCallback?.(100);
      this.savePDF(command);
    } catch (error) {
      console.error('❌ [ExportPDFService] Error in exportEventListPDF:', error);
      throw error;
    }
  }

  async generateBannerImage(
    command: ExportTripPDFCommand,
    totalItemsCount: number
  ) {
    const base64Imgs = await this.cropImages(command, totalItemsCount);
    const dynamicHTML = this.createBannerDiv(base64Imgs);
    document.body.appendChild(dynamicHTML);

    const div = document.getElementById('ig-tp-pdf-trip-banner');

    // Ensure html2canvas is available
    if (!window.html2canvas) {
      throw new Error('html2canvas is not available');
    }

    const canvas = await window.html2canvas(div);
    command.topImage = canvas.toDataURL();
  }

  async cropImages(command: ExportTripPDFCommand, totalItemsCount: number) {
    let base64Imgs = [],
      imageInBanner = totalItemsCount;
    if (totalItemsCount >= 4) {
      imageInBanner = 4;
    }

    let index = 0;
    for (const day of command.trip.days) {
      for (const attr of day.attractions) {
        if (base64Imgs.length >= 4) break;

        let desiredWidth = 840,
          desiredHeight = 480;
        if (base64Imgs.length === 0 && totalItemsCount > 1) {
          desiredWidth = 420;
          desiredHeight = 480;
        }
        if (base64Imgs.length === 1) {
          if (totalItemsCount === 2) {
            desiredWidth = 420;
            desiredHeight = 480;
          }
          if (totalItemsCount > 2) {
            desiredWidth = 420;
            desiredHeight = 240;
          }
        }
        if (base64Imgs.length === 2) {
          if (totalItemsCount === 3) {
            desiredWidth = 420;
            desiredHeight = 240;
          }
          if (totalItemsCount > 3) {
            desiredWidth = 210;
            desiredHeight = 240;
          }
        }
        if (base64Imgs.length === 3) {
          desiredWidth = 210;
          desiredHeight = 240;
        }

        const base64 = await this.imageService.cropImage(
          attr.item.cover.source,
          desiredWidth * 2,
          desiredHeight * 2
        );
        index++;
        command.progressCallback(
          (index / (totalItemsCount + imageInBanner)) * 100
        );
        base64Imgs.push(base64);
      }
    }
    return base64Imgs;
  }

  private async drawCalendarMonthPdfPageHeader(
    command: ExportCalendarMonthPDFCommand,
    monthLabel: string,
    isFirstPage: boolean
  ) {
    if (isFirstPage) {
      await this.drawCalendarMonthPdfTopBanner(command);
      this.y = 70;

      this.doc.setTextColor(15, 23, 42);
      this.doc.setFont(undefined, 'bolditalic');
      this.doc.setFontSize(20);
      this.doc.text(command.title || 'Calendar', this.getCenteredTextX(command.title || 'Calendar'), this.y);

      if (command.subtitle) {
        this.doc.setFont(undefined, 'italic');
        this.doc.setFontSize(10.5);
        const subtitle = this.replaceSpecialChar(command.subtitle, false);
        this.doc.text(subtitle, this.getCenteredTextX(subtitle), this.y + 7);
        this.y += 9;
      }

      this.y += 10;
      } else {
        this.y = 16;
        this.doc.setTextColor(15, 23, 42);
        this.doc.setFont(undefined, 'bold');
        this.doc.setFontSize(14);
      const continuedTitle = command.title
        ? `${command.title} Calendar`
        : 'Calendar';
      this.doc.text(continuedTitle, this.getCenteredTextX(continuedTitle), this.y);
      this.y += 8;
    }

    this.doc.setFont(undefined, 'bold');
    this.doc.setFontSize(18);
    this.doc.setTextColor(31, 41, 55);
    this.doc.text(monthLabel, this.getCenteredTextX(monthLabel), this.y);
    this.y += 8;

    this.drawCalendarMonthPdfWeekdayHeader();
  }

  private async drawCalendarMonthPdfTopBanner(command: ExportCalendarMonthPDFCommand) {
    const bannerHeight = 60;
    const topImageSource = command.topImage || ConfigService.config.PDF.PDFTopEventBackgroundImage;

    try {
      const cropped = await this.imageService.cropImage(
        this.imageService.imageSourceUrl(topImageSource),
        1188,
        337
      );
      await this.loadImage(cropped, PDFImageFor.top);
      const topData = this.pdfImageBase64.find((x) => x.imageType === PDFImageFor.top);
      if (topData?.base64) {
        const size = await this.imageService.getImageSize(topData.base64);
        const height = this.getRelativeHeight(size.width, size.height, 210);
        const topImageFormat = this.getImageFormat(topData.base64);
        this.doc.addImage(topData.base64, topImageFormat, 0, 0, 210, Math.min(height, bannerHeight));
      } else {
        this.drawHeaderBannerRect(command.mainColor, bannerHeight);
      }
    } catch (_error) {
      this.drawHeaderBannerRect(command.mainColor, bannerHeight);
    }

    if (command.logo) {
      await this.loadImage(command.logo, PDFImageFor.logo);
      await this.drawCalendarMonthPdfLogoOverlay(bannerHeight);
    }
  }

  private drawCalendarMonthPdfWeekdayHeader() {
    const tableX = 8;
    const tableWidth = 194;
    const cellWidth = tableWidth / 7;
    const headerHeight = 8;
    const labels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    this.doc.setDrawColor(209, 213, 219);
    this.doc.setLineWidth(0.2);
    this.doc.setFillColor(255, 255, 255);
    this.doc.rect(tableX, this.y, tableWidth, headerHeight, 'FD');

    labels.forEach((label, index) => {
      const x = tableX + index * cellWidth;
      this.doc.rect(x, this.y, cellWidth, headerHeight, 'S');
      this.doc.setFont(undefined, 'normal');
      this.doc.setFontSize(8.6);
      this.doc.setTextColor(55, 65, 81);
      const textWidth = this.getTextWidth(label, 8.6);
      this.doc.text(x + (cellWidth - textWidth) / 2, this.y + 5, label);
    });

    this.y += headerHeight;
  }

  private drawCalendarMonthPdfWeeks(
    command: ExportCalendarMonthPDFCommand,
    model: CalendarMonthPdfModel
  ) {
    const weekStates = model.weeks.map((week) => this.createCalendarMonthPdfWeekState(week));

    for (let weekIndex = 0; weekIndex < weekStates.length; weekIndex++) {
      const weekState = weekStates[weekIndex];

      while (
        weekState.some(
          (cell) => cell.showDayNumber || cell.entryGroupIndex < cell.entryGroups.length
        )
      ) {
        const contentBottomY = this.getContentBottomY();
        const adjustedAvailableHeight = contentBottomY - this.y;
        const segment = this.planCalendarMonthPdfWeekSegment(weekState, adjustedAvailableHeight);

        if (!segment) {
          this.doc.addPage();
          this.pageNumber++;
          this.drawCalendarMonthPdfFooter(command);
          this.drawCalendarMonthPdfPageHeader(command, model.monthLabel, false);
          continue;
        }

        this.drawCalendarMonthPdfWeekSegment(weekState, segment);

        if (
          weekState.some(
            (cell) => cell.showDayNumber || cell.entryGroupIndex < cell.entryGroups.length
          )
        ) {
          this.doc.addPage();
          this.pageNumber++;
          this.drawCalendarMonthPdfFooter(command);
          this.drawCalendarMonthPdfPageHeader(command, model.monthLabel, false);
        }
      }

      if (weekIndex < weekStates.length - 1 && this.getContentBottomY() - this.y < 22) {
        this.doc.addPage();
        this.pageNumber++;
        this.drawCalendarMonthPdfFooter(command);
        this.drawCalendarMonthPdfPageHeader(command, model.monthLabel, false);
      }
    }
  }

  private async drawCalendarMonthPdfLogoOverlay(bannerHeight: number) {
    const logoData = this.pdfImageBase64.find(
      (x) => x.imageType === PDFImageFor.logo
    );

    if (!logoData?.base64) return;

    const maxWidth = 70;
    const maxHeight = 18;
    const size = await this.imageService.getImageSize(logoData.base64);
    if (!size?.width || !size?.height) return;

    let width = this.getRelativeWidth(size.width, size.height, maxHeight);
    let height = maxHeight;

      if (width > maxWidth) {
        width = maxWidth;
        height = this.getRelativeHeight(size.width, size.height, maxWidth);
      }

      const x = 210 - width - 12;
      const y = bannerHeight - height - 0.4;
      this.doc.addImage(
        logoData.base64,
        'PNG',
        x,
        y,
        width,
        height
      );
  }

  private drawCalendarMonthPdfFooter(command: ExportCalendarMonthPDFCommand) {
    const footerHeight = 14;
    const footerY = 297 - footerHeight;
    const link = this.resolveCalendarMonthPdfLink(command);
    const maxLinkWidth = 202;
    const titleFontSize = 12.2;
    let linkFontSize = 9.6;

    while (linkFontSize > 7.2 && this.getTextWidth(link, linkFontSize) > maxLinkWidth) {
      linkFontSize -= 0.2;
    }

    this.doc.setFontSize(linkFontSize);
    const linkLines =
      this.getTextWidth(link, linkFontSize) <= maxLinkWidth
        ? [link]
        : this.doc.splitTextToSize(link, maxLinkWidth).slice(0, 2);
    const lines = ['MORE EVENTS:', ...linkLines];

    const color = hexToRgb(command.mainColor || '#5cbcff');
    this.doc.setFillColor(color.r, color.g, color.b);
    this.doc.rect(0, footerY, 210, footerHeight, 'F');
    this.doc.setTextColor(255, 255, 255);

    const hasWrappedLink = linkLines.length > 1;
    const titleHeight = 3.7;
    const titleGap = hasWrappedLink ? 0.6 : 0.8;
    const linkLineHeight = hasWrappedLink ? 2.7 : 3.2;
    const totalBlockHeight =
      titleHeight + titleGap + linkLineHeight * linkLines.length;
    let y = footerY + (footerHeight - totalBlockHeight) / 2 + titleHeight;
    lines.forEach((line, index) => {
      this.doc.setFont(undefined, index === 0 ? 'bold' : 'normal');
      this.doc.setFontSize(index === 0 ? titleFontSize : linkFontSize);
      const textWidth = this.getTextWidth(line, index === 0 ? titleFontSize : linkFontSize);
      const x = (210 - textWidth) / 2;
      this.doc.text(line, x, index === 0 ? y - 1 : y);
      if (index > 0) {
        this.doc.link(x, y - 3.1, textWidth, hasWrappedLink ? 3.4 : 4, { url: link });
      }
      y += index === 0 ? titleGap + linkLineHeight : linkLineHeight;
    });
  }

  private resolveCalendarMonthPdfLink(command: ExportCalendarMonthPDFCommand) {
    if (!command.eventCalendarURL) {
      return window.location.href;
    }

    return command.eventCalendarURL.startsWith('http')
      ? command.eventCalendarURL
      : `https://${command.eventCalendarURL}`;
  }

  private createCalendarMonthPdfWeekState(week: CalendarMonthPdfWeek): CalendarMonthPdfCellState[] {
    return week.days.map((day) => ({
      day,
      entryGroups: this.createCalendarMonthPdfEntryBlockGroups(
        day.entries.filter((entry) => !entry.spansMultipleDays && !entry.isAllDay)
      ),
      entryGroupIndex: 0,
      showDayNumber: true,
      showSpanRows: true,
    }));
  }

  private createCalendarMonthPdfEntryBlockGroups(
    entries: CalendarMonthPdfEntry[]
  ): CalendarMonthPdfEntryBlockGroup[] {
    const groups: CalendarMonthPdfEntryBlockGroup[] = [];
    const calendarCommand = this.command as ExportCalendarMonthPDFCommand | undefined;
    const editorChoiceLabel = this.replaceSpecialChar(
      calendarCommand?.editorChoiceLabel || 'Featured Event',
      false
    );
    const shouldHideEditorChoice = !!calendarCommand?.hideEditorChoice;

    entries.forEach((entry) => {
      const blocks: CalendarMonthPdfTextBlock[] = [];
      if (entry.isChoice && !shouldHideEditorChoice && editorChoiceLabel) {
        blocks.push({
          text: editorChoiceLabel.toUpperCase(),
          fontSize: 5.8,
          lineSpacing: 2.4,
          color: '#ffffff',
          fontStyle: 'bold',
          marginTop: blocks.length ? 1.9 : 1.2,
          fillColor: calendarCommand?.editorChoiceColor || '#3b82f6',
          horizontalPadding: 1.6,
          verticalPadding: 0.8,
        });
      }

      if (entry.timeText) {
        blocks.push({
          text: entry.timeText,
          fontSize: 6.8,
          lineSpacing: 2.8,
          color: '#6b7280',
          fontStyle: 'normal',
          marginTop: blocks.length ? 1.9 : 1.2,
        });
      }

      blocks.push({
        text: entry.title,
        fontSize: 7.5,
        lineSpacing: 3.3,
        color: '#111827',
        fontStyle: 'bold',
        marginTop: entry.timeText ? 0.55 : (blocks.length ? 1.9 : 1.2),
      });

      groups.push({ blocks });
    });

    return groups;
  }

  private async drawEventListPdfHeader(command: ExportEventPlacePDFCommand) {
    const bannerHeight = 38;
    const title = this.replaceSpecialChar(
      command.title || this.defaultTitleText.event.text,
      false
    );
    const selectedDateRange = this.getEventPlacePdfDateRangeLabel(command);

    if (command.topImage) {
      try {
        const cropped = await this.imageService.cropImage(
          this.imageService.imageSourceUrl(command.topImage),
          1188,
          300
        );
        await this.loadImage(cropped, PDFImageFor.top);
        const topData = this.pdfImageBase64.find((x) => x.imageType === PDFImageFor.top);
        if (topData?.base64) {
          const size = await this.imageService.getImageSize(topData.base64);
          const height = this.getRelativeHeight(size.width, size.height, 210);
          const topImageFormat = this.getImageFormat(topData.base64);
          this.doc.addImage(topData.base64, topImageFormat, 0, 0, 210, Math.min(height, bannerHeight));
        } else {
          this.drawHeaderBannerRect(command.mainColor, bannerHeight);
        }
      } catch (_error) {
        this.drawHeaderBannerRect(command.mainColor, bannerHeight);
      }
    } else {
      this.drawHeaderBannerRect(command.mainColor, bannerHeight);
    }

    if (command.logo) {
      await this.loadImage(command.logo, PDFImageFor.logo);
      await this.drawCalendarMonthPdfLogoOverlay(bannerHeight);
    }

    this.y = bannerHeight + 11;
    this.doc.setFont(undefined, 'bold');
    this.doc.setFontSize(18);
    this.doc.setTextColor(55, 65, 81);
    this.doc.text(title, this.getCenteredTextX(title), this.y);

    if (selectedDateRange) {
      const dateRangeText = this.replaceSpecialChar(selectedDateRange, false);
      this.doc.setFont(undefined, 'normal');
      this.doc.setFontSize(9.5);
      this.doc.setTextColor(107, 114, 128);
      this.doc.text(dateRangeText, this.getCenteredTextX(dateRangeText), this.y + 6.5);
      this.y += 10;
    } else {
      this.y += 6;
    }

    if (command.subtitle) {
      const subtitle = this.replaceSpecialChar(command.subtitle, false);
      this.doc.setFont(undefined, 'normal');
      this.doc.setFontSize(8.8);
      this.doc.setTextColor(125, 135, 148);
      this.doc.text(subtitle, this.getCenteredTextX(subtitle), this.y + 5.5);
      this.y += 9;
    }
  }

  private getEventPlacePdfDateRangeLabel(command: ExportEventPlacePDFCommand): string {
    if (!command?.from || !command?.to) return '';

    return `${this.formatEventPlacePdfDate(command.from)} - ${this.formatEventPlacePdfDate(command.to)}`;
  }

  private formatEventPlacePdfDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  }

  private drawEventListPdfPageBackground(startY: number) {
    const contentBottomY = this.getContentBottomY();
    const fillTop = Math.max(0, startY);
    const fillHeight = Math.max(0, contentBottomY - fillTop);
    this.doc.setFillColor(238, 245, 249);
    this.doc.rect(0, fillTop, 210, fillHeight, 'F');
  }

  private drawEventListPdfRow(item: VisitorEvent, command: ExportEventPlacePDFCommand) {
    const x = 10;
    const width = 190;
    const buttonWidth = 38;
    const buttonHeight = 12;
    const cardPaddingX = 5;
    const cardPaddingY = 5;
    const gap = 6;
    const contentWidth = width - buttonWidth - cardPaddingX * 2 - gap - 4;
    const title = this.replaceSpecialChar(item?.title || item?.name || '(Untitled event)', false);
    const timeText = this.replaceSpecialChar(this.getEventListPdfTime(item), false);
    const addressText = this.replaceSpecialChar(this.getEventListPdfAddress(item), false);
    const buttonText = 'View Details';
    const link = this.getEventListPdfDetailsLink(item, command);
    const visualCenterAdjustment = 3.2;
    const titleCommand = {
      text: title,
      x: x + cardPaddingX,
      y: 0,
      width: contentWidth,
      maxLines: 2,
      lineSpacing: 6,
      fontSize: 16,
      color: '#6b7280',
      fontStyle: 'normal',
    } as DrawTextCommand;
    const timeCommand = {
      text: timeText,
      x: x + cardPaddingX,
      y: 0,
      width: contentWidth,
      maxLines: 1,
      lineSpacing: 4.5,
      fontSize: 10.8,
      color: '#10b8cf',
      fontStyle: 'bold',
    } as DrawTextCommand;
    const addressCommand = {
      text: addressText,
      x: x + cardPaddingX,
      y: 0,
      width: contentWidth,
      maxLines: 2,
      lineSpacing: 4.5,
      fontSize: 10.5,
      color: '#6b7280',
      fontStyle: 'normal',
    } as DrawTextCommand;

    let measuredY = this.drawText(
      {
        ...titleCommand,
        y: 0,
      },
      true
    );
    measuredY = this.drawText(
      {
        ...timeCommand,
        y: measuredY + 1.5,
      },
      true
    );
    measuredY = this.drawText(
      {
        ...addressCommand,
        y: measuredY + 1.4,
      },
      true
    );

    const textBlockHeight = measuredY;
    const rowHeight = Math.max(28, textBlockHeight + cardPaddingY * 2 + 0.5);
    const contentBottomY = this.getContentBottomY();
    if (this.y + rowHeight > contentBottomY) {
      this.addPage(command);
      this.drawEventListPdfPageBackground(12);
    }

    const buttonX = x + width - buttonWidth - 4;
    const buttonY = this.y + (rowHeight - buttonHeight) / 2;

    this.doc.setFillColor(255, 255, 255);
    this.doc.setDrawColor(213, 225, 232);
    this.doc.setLineWidth(0.2);
    this.doc.roundedRect(x, this.y, width, rowHeight, 4, 4, 'FD');
    this.drawEventListPdfEditorChoiceBadge(item, command, x, width);

    let textY = this.y + (rowHeight - textBlockHeight) / 2 + visualCenterAdjustment;
    textY = this.drawText({
      ...titleCommand,
      y: textY,
    } as DrawTextCommand);

    textY = this.drawText({
      ...timeCommand,
      y: textY + 1.5,
    } as DrawTextCommand);

    this.drawText({
      ...addressCommand,
      y: textY + 1.4,
    } as DrawTextCommand);

    const buttonColor = hexToRgb(command.mainColor || '#5cbcff');
    this.doc.setFillColor(buttonColor.r, buttonColor.g, buttonColor.b);
    this.doc.roundedRect(buttonX, buttonY, buttonWidth, buttonHeight, 2, 2, 'F');
    this.doc.setFont(undefined, 'bold');
    this.doc.setFontSize(9.5);
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(
      buttonText,
      buttonX + (buttonWidth - this.getTextWidth(buttonText, 9.5)) / 2,
      buttonY + 7.4
    );
    if (link) {
      this.doc.link(buttonX, buttonY, buttonWidth, buttonHeight, { url: link });
    }

    this.y += rowHeight + 4;
  }

  private drawEventListPdfEditorChoiceBadge(
    item: VisitorEvent,
    command: ExportEventPlacePDFCommand,
    cardX: number,
    cardWidth: number
  ) {
    if (!item?.isChoice || command.hideEditorChoice) {
      return;
    }

    const label = this.replaceSpecialChar(command.editorChoiceLabel || 'Featured Event', false);
    const badgeColor = hexToRgb(command.editorChoiceColor || '#f59e0b');
    const fontSize = 8.5;
    const horizontalPadding = 3.4;
    const badgeHeight = 7.2;
    const maxBadgeWidth = 48;

    this.doc.setFont(undefined, 'bold');
    this.doc.setFontSize(fontSize);

    let badgeText = label;
    let badgeWidth = this.getTextWidth(badgeText, fontSize) + horizontalPadding * 2;

    if (badgeWidth > maxBadgeWidth) {
      while (badgeText.length > 8) {
        badgeText = `${badgeText.slice(0, -2)}...`;
        badgeWidth = this.getTextWidth(badgeText, fontSize) + horizontalPadding * 2;
        if (badgeWidth <= maxBadgeWidth) {
          break;
        }
      }
      badgeWidth = Math.min(maxBadgeWidth, badgeWidth);
    }

    const badgeX = cardX + cardWidth - badgeWidth - 0.3;
    const badgeY = this.y + 0.3;

    this.doc.setFillColor(badgeColor.r, badgeColor.g, badgeColor.b);
    this.doc.roundedRect(badgeX, badgeY, badgeWidth, badgeHeight, 0, 0, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.text(
      badgeText,
      badgeX + (badgeWidth - this.getTextWidth(badgeText, fontSize)) / 2,
      badgeY + 4.9
    );
  }

  private getEventListPdfTime(item: VisitorEvent): string {
    if (item?.eventTimes?.length > 1) {
      return 'Multiple Dates Event';
    }

    return (
      item?.timeOneLine ||
      item?.eventTimes?.[0]?.timeOneLine ||
      item?.startTimeFormatted ||
      ''
    );
  }

  private getEventListPdfAddress(item: VisitorEvent): string {
    if (item?.isOnlineEvent) {
      return 'Online Event';
    }

    const components = item?.address?.addressComponents || [];
    if (components.length) {
      const findComponent = (types: string[]) => {
        const comp = components.find((component) => types.every((type) => component.types?.includes(type)));
        return comp?.short_name ?? comp?.shortText ?? '';
      };

      const streetNumber = findComponent(['street_number']);
      const route = findComponent(['route']);
      const city = findComponent(['locality', 'political']);
      const state = findComponent(['administrative_area_level_1', 'political']);

      if (streetNumber) {
        const street = [streetNumber, route].filter(Boolean).join(' ');
        const line = [street, city, state].filter(Boolean).join(', ');
        if (line) return line;
      }
    }

    const fullAddress = item?.address?.address || item?.venue || '(no location)';
    return fullAddress
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 3)
      .join(', ');
  }

  private getEventListPdfDetailsLink(item: VisitorEvent, command: ExportEventPlacePDFCommand): string {
    const baseLink = this.resolveEventPlaceFooterLink(command);
    if (!item?.nameId) {
      return baseLink;
    }

    try {
      const url = new URL(baseLink);
      url.searchParams.set('imgoing-event', item.nameId);
      return url.toString();
    } catch (_error) {
      const separator = baseLink.includes('?') ? '&' : '?';
      return `${baseLink}${separator}imgoing-event=${encodeURIComponent(item.nameId)}`;
    }
  }

  private planCalendarMonthPdfWeekSegment(
    weekState: CalendarMonthPdfCellState[],
    availableHeight: number
  ) {
    const cellPaddingTop = 2;
    const cellPaddingBottom = 2;
    const innerWidth = 24.5;
    const dayNumberHeight = 5.2;
    const spanRowHeight = 5.8;
    const spanRowGap = 1;
    const spanRows = this.createCalendarMonthPdfWeekSpanRows(weekState);
    const shouldDrawSpanRows = weekState.some((cell) => cell.showSpanRows) && !!spanRows.length;
    const spanAreaHeight = spanRows.length
      ? spanRows.length * spanRowHeight + (spanRows.length - 1) * spanRowGap + 1
      : 0;
    const plannedCells: CalendarMonthPdfPlannedCell[] = [];
    let rowHeight = 18;

    if (availableHeight < 16) {
      return null;
    }

    for (const cell of weekState) {
      let usedHeight = cellPaddingTop;
      let drawDayNumber = false;
      const plannedEntryGroups: CalendarMonthPdfEntryBlockGroup[] = [];

      if (cell.showDayNumber) {
        drawDayNumber = true;
        usedHeight += dayNumberHeight;
        if (shouldDrawSpanRows) {
          usedHeight += spanAreaHeight;
        }
      }

      if (usedHeight + cellPaddingBottom > availableHeight) {
        return null;
      }

      while (cell.entryGroupIndex + plannedEntryGroups.length < cell.entryGroups.length) {
        const entryGroup = cell.entryGroups[cell.entryGroupIndex + plannedEntryGroups.length];
        const entryGroupHeight = this.measureCalendarMonthPdfEntryBlockGroup(entryGroup, innerWidth);

        if (usedHeight + entryGroupHeight + cellPaddingBottom > availableHeight) {
          break;
        }

        usedHeight += entryGroupHeight;
        plannedEntryGroups.push(entryGroup);
      }

      if (
        !drawDayNumber &&
        !plannedEntryGroups.length &&
        cell.entryGroupIndex < cell.entryGroups.length
      ) {
        return null;
      }

      rowHeight = Math.max(rowHeight, usedHeight + cellPaddingBottom);
      plannedCells.push({
        drawDayNumber,
        entryGroups: plannedEntryGroups,
        usedHeight,
      });
    }

    return {
      rowHeight,
      plannedCells,
      spanRows,
      drawSpanRows: shouldDrawSpanRows,
    };
  }

  private measureCalendarMonthPdfTextBlock(
    block: CalendarMonthPdfTextBlock,
    width: number
  ) {
    this.doc.setFontSize(block.fontSize);
    this.doc.setFont(undefined, block.fontStyle || 'normal');
    const horizontalPadding = block.horizontalPadding || 0;
    const verticalPadding = block.verticalPadding || 0;
    const measureWidth = Math.max(1, width - horizontalPadding * 2);
    const lines =
      this.doc.splitTextToSize(this.replaceSpecialChar(block.text, false), measureWidth) || [''];
    return block.marginTop + lines.length * block.lineSpacing + verticalPadding * 2;
  }

  private measureCalendarMonthPdfEntryBlockGroup(
    entryGroup: CalendarMonthPdfEntryBlockGroup,
    width: number
  ) {
    return (entryGroup.blocks || []).reduce(
      (total, block) => total + this.measureCalendarMonthPdfTextBlock(block, width),
      0
    );
  }

  private drawCalendarMonthPdfWeekSegment(
    weekState: CalendarMonthPdfCellState[],
    segment: {
      rowHeight: number;
      plannedCells: CalendarMonthPdfPlannedCell[];
      spanRows: CalendarMonthPdfWeekSpanRow[];
      drawSpanRows: boolean;
    }
  ) {
    const tableX = 8;
    const tableWidth = 194;
    const cellWidth = tableWidth / 7;
    const innerWidth = cellWidth - 3.2;
    const rowTop = this.y;
    const spanRowHeight = 5.8;
    const spanRowGap = 1;
    const spanAreaHeight =
      segment.spanRows.length * spanRowHeight +
      Math.max(0, segment.spanRows.length - 1) * spanRowGap +
      1;

    segment.plannedCells.forEach((plannedCell, index) => {
      const x = tableX + index * cellWidth;
      const day = weekState[index].day;

      this.doc.setDrawColor(209, 213, 219);
      this.doc.setLineWidth(0.2);

      if (!day.inMonth) {
        this.doc.setFillColor(249, 250, 251);
      } else {
        this.doc.setFillColor(255, 255, 255);
      }

      this.doc.rect(x, rowTop, cellWidth, segment.rowHeight, 'FD');

      let textY = rowTop + 3.5;

      if (plannedCell.drawDayNumber) {
        this.doc.setFont(undefined, 'bold');
        this.doc.setFontSize(10);
        this.doc.setTextColor(day.inMonth ? 17 : 156, day.inMonth ? 24 : 163, day.inMonth ? 39 : 175);
        this.doc.text(day.dayOfMonth.toString(), x + 1.2, textY);
        textY += 4.5;
        weekState[index].showDayNumber = false;
      }

      if (segment.drawSpanRows) {
        textY += spanAreaHeight;
      }

      plannedCell.entryGroups.forEach((entryGroup) => {
        entryGroup.blocks.forEach((block) => {
          textY += block.marginTop;
          const text = this.replaceSpecialChar(block.text, false);
          const horizontalPadding = block.horizontalPadding || 0;
          const verticalPadding = block.verticalPadding || 0;
          const textWidth = Math.max(1, innerWidth - horizontalPadding * 2);
          let drawY = textY;

          if (block.fillColor) {
            this.doc.setFontSize(block.fontSize);
            this.doc.setFont(undefined, block.fontStyle || 'normal');
            const lines = this.doc.splitTextToSize(text, textWidth) || [''];
            const blockHeight = lines.length * block.lineSpacing + verticalPadding * 2;
            const widestLine = lines.reduce(
              (maxWidth, line) => Math.max(maxWidth, this.getTextWidth(line, block.fontSize)),
              0
            );
            const fillWidth =
              block.fillMode === 'fullWidth'
                ? innerWidth
                : Math.min(innerWidth, widestLine + horizontalPadding * 2);
            const fillColor = hexToRgb(block.fillColor || '') || hexToRgb('#a81df7');

            this.doc.setFillColor(fillColor.r, fillColor.g, fillColor.b);
            this.doc.roundedRect(x + 1.2, drawY, fillWidth, blockHeight, 1.2, 1.2, 'F');
            drawY += verticalPadding + block.lineSpacing - 0.35;
          }

          textY = this.drawText(
            {
              text,
              x: x + 1.2 + horizontalPadding,
              y: drawY,
              width: textWidth,
              lineSpacing: block.lineSpacing,
              fontSize: block.fontSize,
              fontStyle: block.fontStyle,
              color: block.color,
            } as DrawTextCommand
          );
          textY += verticalPadding;
        });
        weekState[index].entryGroupIndex++;
      });
    });

    if (segment.drawSpanRows) {
      this.drawCalendarMonthPdfWeekSpanRows(
        weekState,
        segment.spanRows,
        rowTop,
        tableX,
        cellWidth,
        spanRowHeight,
        spanRowGap
      );
      weekState.forEach((cell) => {
        cell.showSpanRows = false;
      });
    }

    this.y += segment.rowHeight;
  }

  private createCalendarMonthPdfWeekSpanRows(
    weekState: CalendarMonthPdfCellState[]
  ): CalendarMonthPdfWeekSpanRow[] {
    const spansByKey = new Map<string, CalendarMonthPdfWeekSpan>();

    weekState.forEach((cell, dayIndex) => {
      cell.day.entries.forEach((entry) => {
        if ((!entry.spansMultipleDays && !entry.isAllDay) || !entry.spanKey) {
          return;
        }

        const existing = spansByKey.get(entry.spanKey);
        if (existing) {
          existing.startDayIndex = Math.min(existing.startDayIndex, dayIndex);
          existing.endDayIndex = Math.max(existing.endDayIndex, dayIndex);
          return;
        }

        spansByKey.set(entry.spanKey, {
          key: entry.spanKey,
          title: entry.title,
          isChoice: !!entry.isChoice,
          sortValue: entry.sortValue,
          startDayIndex: dayIndex,
          endDayIndex: dayIndex,
        });
      });
    });

    const spans = Array.from(spansByKey.values()).sort((a, b) => {
      if (a.sortValue !== b.sortValue) {
        return a.sortValue - b.sortValue;
      }

      if (a.startDayIndex !== b.startDayIndex) {
        return a.startDayIndex - b.startDayIndex;
      }

      return a.title.localeCompare(b.title);
    });

    const rows: CalendarMonthPdfWeekSpanRow[] = [];
    spans.forEach((span) => {
      let row = rows.find((candidate) =>
        candidate.spans.every(
          (existingSpan) =>
            span.endDayIndex < existingSpan.startDayIndex ||
            span.startDayIndex > existingSpan.endDayIndex
        )
      );

      if (!row) {
        row = { spans: [] };
        rows.push(row);
      }

      row.spans.push(span);
    });

    return rows;
  }

  private drawCalendarMonthPdfWeekSpanRows(
    weekState: CalendarMonthPdfCellState[],
    spanRows: CalendarMonthPdfWeekSpanRow[],
    rowTop: number,
    tableX: number,
    cellWidth: number,
    spanRowHeight: number,
    spanRowGap: number
  ) {
    if (!spanRows.length) {
      return;
    }

    const barXInset = 1;
    const barWidthInset = 2;
    const barYBase = rowTop + 7.4;

    spanRows.forEach((spanRow, rowIndex) => {
      const barY = barYBase + rowIndex * (spanRowHeight + spanRowGap);

      spanRow.spans.forEach((span) => {
        const barX = tableX + span.startDayIndex * cellWidth + barXInset;
        const spanDays = span.endDayIndex - span.startDayIndex + 1;
        const barWidth = spanDays * cellWidth - barWidthInset;
        const fillColor = hexToRgb('#ddd4aa');
        const textColor = hexToRgb('#3f3720');
        const text = this.replaceSpecialChar(span.title, false);

        this.doc.setFillColor(fillColor.r, fillColor.g, fillColor.b);
        this.doc.roundedRect(barX, barY, barWidth, spanRowHeight, 1.2, 1.2, 'F');

        this.doc.setFont(undefined, 'bold');
        this.doc.setFontSize(7.3);
        this.doc.setTextColor(textColor.r, textColor.g, textColor.b);

        const textX = barX + 1.2;
        const textY = barY + 3.85;
        const maxTextWidth = Math.max(1, barWidth - 2.4);
        const titleLines = this.doc.splitTextToSize(text, maxTextWidth) || [''];
        const title = titleLines[0] || '';
        this.doc.text(title, textX, textY);
      });
    });
  }

  // UPDATED: Create document with better error handling
  private createDoc(command: ExportPDFCommandBase) {
    try {
      if (!window.jsPDF) {
        throw new Error('jsPDF not available');
      }

      this.doc = new window.jsPDF('p', 'mm', 'a4');
      this.drawFooter(command);
      this.pageNumber = 1;
      console.log('✅ [ExportPDFService] jsPDF document created successfully');
    } catch (error) {
      console.error(
        '❌ [ExportPDFService] Error creating jsPDF document:',
        error
      );
      throw new Error(`Failed to create jsPDF document: ${error.message}`);
    }
  }

  // Rest of your existing methods remain the same...
  // (I'm keeping the essential structure but you can add all your existing methods here)

  createBannerDiv(imageUrls: string[]) {
    const container = document.createElement('div');
    container.className =
      'fixed z-[-1] translate-x-[-3000px] top-2 h-fit w-fit';
    container.id = 'ig-tp-pdf-trip-banner';

    const flexContainer = document.createElement('div');
    flexContainer.className = 'flex items-center justify-center h-fit w-fit';

    const innerContainer = document.createElement('div');
    innerContainer.className =
      'w-[1260px] h-[720px] flex gap-1.5 bg-white shadow-lg';

    if (imageUrls.length === 0) {
      return null;
    }

    const firstImageDiv = document.createElement('div');
    firstImageDiv.className =
      imageUrls.length === 1 ? 'w-full h-full' : 'w-1/2 h-full';

    const firstImage = document.createElement('img');
    firstImage.src = imageUrls[0];
    firstImage.className = 'w-full h-full object-cover';
    firstImage.alt = 'Left image';
    firstImageDiv.appendChild(firstImage);
    innerContainer.appendChild(firstImageDiv);

    if (imageUrls.length > 1) {
      const rightHalfDiv = document.createElement('div');
      rightHalfDiv.className =
        imageUrls.length === 2
          ? 'w-1/2 h-full'
          : 'w-1/2 h-full flex flex-col gap-1.5';

      if (imageUrls.length === 2) {
        const secondImageDiv = document.createElement('div');
        secondImageDiv.className = 'h-full';
        const secondImage = document.createElement('img');
        secondImage.src = imageUrls[1];
        secondImage.className = 'w-full h-full object-cover';
        secondImage.alt = 'Right image';
        secondImageDiv.appendChild(secondImage);
        rightHalfDiv.appendChild(secondImageDiv);
      } else {
        const topImageDiv = document.createElement('div');
        topImageDiv.className = 'h-1/2';
        const topImage = document.createElement('img');
        topImage.src = imageUrls[1];
        topImage.className = 'w-full h-full object-cover';
        topImage.alt = 'Right top image';
        topImageDiv.appendChild(topImage);
        rightHalfDiv.appendChild(topImageDiv);

        const bottomImagesDiv = document.createElement('div');
        bottomImagesDiv.className = 'h-1/2 flex gap-1.5';

        if (imageUrls.length === 3) {
          const bottomImageDiv = document.createElement('div');
          bottomImageDiv.className = 'w-full h-full';
          const bottomImage = document.createElement('img');
          bottomImage.src = imageUrls[2];
          bottomImage.className = 'w-full h-full object-cover';
          bottomImage.alt = 'Bottom image';
          bottomImageDiv.appendChild(bottomImage);
          bottomImagesDiv.appendChild(bottomImageDiv);
        } else if (imageUrls.length > 3) {
          const bottomLeftImageDiv = document.createElement('div');
          bottomLeftImageDiv.className = 'w-1/2 h-full';
          const bottomLeftImage = document.createElement('img');
          bottomLeftImage.src = imageUrls[2];
          bottomLeftImage.className = 'w-full h-full object-cover';
          bottomLeftImage.alt = 'Bottom left image';
          bottomLeftImageDiv.appendChild(bottomLeftImage);
          bottomImagesDiv.appendChild(bottomLeftImageDiv);

          const bottomRightImageDiv = document.createElement('div');
          bottomRightImageDiv.className = 'w-1/2 h-full';
          const bottomRightImage = document.createElement('img');
          bottomRightImage.src = imageUrls[3];
          bottomRightImage.className = 'w-full h-full object-cover';
          bottomRightImage.alt = 'Bottom right image';
          bottomRightImageDiv.appendChild(bottomRightImage);
          bottomImagesDiv.appendChild(bottomRightImageDiv);
        }
        rightHalfDiv.appendChild(bottomImagesDiv);
      }
      innerContainer.appendChild(rightHalfDiv);
    }

    flexContainer.appendChild(innerContainer);
    container.appendChild(flexContainer);
    return container;
  }

  removeDynamicImage() {
    const element = document.getElementById('test-img');
    if (element) {
      element.remove();
    }
  }

  // Add all your other existing methods here...
  // For brevity, I'm including the key structural methods

  private addNewPageIfNeeded(
    command: ExportPDFCommandBase,
    thresholdY: number
  ) {
    if (this.y > thresholdY) {
      this.addPage(command);
    }
  }

  /**
   * Usable content bottom boundary (keep a small safe gap above footer button).
   */
  private getContentBottomY(): number {
      const pageHeight = this.doc?.internal?.pageSize?.getHeight?.() || 297;
      const footerHeight = 14; // Matches calendar month PDF footer height.
      const safeGap = 2;
      return pageHeight - footerHeight - safeGap;
    }

  private async loadImageForItems(
    items: ItineraryAttraction[] | TripAttraction[],
    totalItemsCount: number,
    imageInBannerCount: number,
    callback: any,
    fallbackImage?: string
  ) {
    for (const item of items) {
      const imageUrl = this.imageService.imageSourceUrl(item.item.cover?.source);
      const directImage = await this.loadDirectS3ImageForPdf(imageUrl);
      let base64 = directImage ? null : await this.loadImageBase64(imageUrl);
      const mapIconUrl = (item as ItineraryAttraction).mapIcon;
      const mapIconBase64 = mapIconUrl
        ? (await this.loadImageBase64(mapIconUrl)) || undefined
        : undefined;
      if (directImage) {
        this.pdfImageBase64.push(
          new PDFImgDataObject(
            PDFImageFor.item,
            null,
            0,
            0,
            item,
            mapIconBase64,
            directImage.data,
            directImage.format
          )
        );
      } else if (!base64) {
        console.log(
          `%cImage not load ${item.item.name}: ${item.item.cover.source}`,
          'color:orange'
        );
        if (fallbackImage) {
          if (fallbackImage.startsWith('http')) {
            base64 = await this.loadImageBase64(fallbackImage);
            base64 &&
              this.pdfImageBase64.push(
                new PDFImgDataObject(PDFImageFor.item, base64, 0, 0, item, mapIconBase64)
              );
          } else if (this.imageService.isBase64Image(fallbackImage)) {
            this.pdfImageBase64.push(
              new PDFImgDataObject(PDFImageFor.item, fallbackImage, 0, 0, item, mapIconBase64)
            );
          }
        }
      } else {
        this.pdfImageBase64.push(
          new PDFImgDataObject(PDFImageFor.item, base64, 0, 0, item, mapIconBase64)
        );
      }

      if (callback) {
        let percentage = Math.round(
          ((this.pdfImageBase64.length + imageInBannerCount) /
            (totalItemsCount + imageInBannerCount + 1)) *
            100
        );
        percentage = Math.min(100, percentage);
        callback(percentage);
      }
    }
  }

  private async drawHeader(command: ExportPDFCommandBase) {
    this.drawHeaderBannerRect(command.mainColor, this.bannerHeight);
    if (command.logo) {
      await this.loadImage(command.logo, PDFImageFor.logo);
      await this.drawLogo();
    }
    if (command.pdfIntro) {
      const offsetX = 210 - this.getTextWidth(command.pdfIntro, 15) - 12;
      this.drawText({
        text: command.pdfIntro,
        x: offsetX,
        y: this.bannerHeight / 2 + 2,
        color: '#FFFFFF',
        fontSize: 15,
        fontStyle: 'bold',
      } as DrawTextCommand);
    }
  }

  private drawHeaderBannerRect(
    mainColor: string,
    height: number,
    pageNumber?: number
  ) {
    let color = hexToRgb(mainColor) ?? { r: 92, g: 188, b: 255 }; // #5cbcff
    this.doc.setFillColor(color.r, color.g, color.b);
    this.doc.rect(0, 0, 210, height, 'F');
    this.doc.setFontSize(12);
    pageNumber && this.doc.text(`Page ${pageNumber}`, 186, height / 2 + 1);
  }

  private async drawLogo() {
    const logoData = this.pdfImageBase64.find(
      (x) => x.imageType === PDFImageFor.logo
    );

    if (logoData && logoData.base64) {
      const targetHeight = 16;
      const size = await this.imageService.getImageSize(logoData.base64);
      const width = this.getRelativeWidth(
        size.width,
        size.height,
        targetHeight
      );
      size.width &&
        this.doc.addImage(
          logoData.base64,
          'PNG',
          8,
          (this.bannerHeight - targetHeight) / 2,
          width,
          targetHeight
        );
    }
  }

  private async drawTopImageAndTitle(
    command: ExportPDFCommandBase,
    maxHeight = 0
  ) {
    command.topImage = !command.topImage
      ? this.imageService.imageSourceUrl(this.defaultTopImages[command.type])
      : this.imageService.imageSourceUrl(command.topImage);

    // crop banner image, so that the first page will show both the image and the map
    const base64 = await this.imageService.cropImage(
      command.topImage,
      840,
      480
    );

    await this.loadImage(base64, PDFImageFor.top);
    const topData = this.pdfImageBase64.find(
      (x) => x.imageType === PDFImageFor.top
    );
    const size = await this.imageService.getImageSize(topData.base64);
    let height = this.getRelativeHeight(size.width, size.height, 210);
    if (maxHeight) {
      height = Math.min(height, maxHeight);
    }
    const topImageFormat = this.getImageFormat(topData.base64);
    size.width && this.doc.addImage(topData.base64, topImageFormat, 0, 0, 210, height);
    this.y = height + 20;
  }

  private calculateTotalAttractionCount(trips: Itinerary[]) {
    let totalItemsCount = 0;
    trips.forEach((trip) => {
      totalItemsCount += trip.attractions.length;
    });
    return totalItemsCount;
  }

  private calculateTotalTripAttractionCount(trip: UserTrip) {
    let totalItemsCount = 0;
    trip.days.forEach((day) => {
      totalItemsCount += day.attractions.length;
    });
    return totalItemsCount;
  }

  private drawNoStops() {
    const startText = '-- No Stops --';
    const startOffsetX = (210 - this.getTextWidth(startText, 13)) / 2;
    this.drawText({
      x: startOffsetX,
      y: this.y + 5,
      text: startText,
    } as DrawTextCommand);
    this.y += 15;
  }

  private drawDescription(description: string) {
    if (description) {
      const boxWidth = 170;
      const yIfDrawText = this.drawText(
        {
          text: description,
          x: 25,
          y: this.y + 6,
          width: boxWidth - 20,
          fontSize: 12,
          lineSpacing: 6,
        } as DrawTextCommand,
        true
      );

      if (yIfDrawText > 287) {
        this.addPage(this.command);
      }

      this.y = this.drawText({
        text: description,
        x: 25,
        y: this.y + 6,
        width: boxWidth - 20,
        fontSize: 12,
        lineSpacing: 6,
      } as DrawTextCommand);
    }
  }

  private drawStart() {
    const startText = '-- START --';
    const startOffsetX = (210 - this.getTextWidth(startText, 13)) / 2;
    this.y = this.drawText({
      x: startOffsetX,
      y: this.y < 27 ? this.y + 5 : this.y + 20,
      text: startText,
      fontStyle: 'bold',
    } as DrawTextCommand);
  }

  private drawEnd() {
    const endText = '-- END --';
    const endOffsetX = (210 - this.getTextWidth(endText, 13)) / 2;
    this.drawText({
      x: endOffsetX,
      y: this.y,
      text: endText,
      fontStyle: 'bold',
    } as DrawTextCommand);
    this.y += 5;
  }

  private drawTripAttractions(day: TripDay) {
    let index = 0;
    const contentBottomY = this.getContentBottomY();
    for (const attr of day.attractions) {
      const item = this.pdfImageBase64.find(
        (x) => x.attraction?.feedId === attr.feedId
      );
      if (!item || !item.base64 || item.imageType !== PDFImageFor.item)
        continue;

      const boxWidth = 170,
        planWidth = 120;

      // Draw pre plans
      if ((item.attraction as TripAttraction).prePlans?.length > 0) {
        const prePlans = (item.attraction as TripAttraction).prePlans;
        for (const prePlan of prePlans) {
          if (this.y > 240) {
            this.addPage(this.command);
          }

          const prePlanText = removeHtmlTags(prePlan.content);
          let y = this.drawText(
            {
              text: `${prePlan.title} · ${prePlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              fontStyle: 'bold',
            } as DrawTextCommand,
            true
          );

          y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
            } as DrawTextCommand,
            true
          );

          if (y + 4 > contentBottomY) {
            this.addPage(this.command);
            y = this.drawText(
              {
                text: `${prePlan.title} · ${prePlan.time || ''}`,
                x: 55,
                y: this.y + 8,
                width: planWidth - 20,
                fontSize: 13,
                lineSpacing: 6,
                fontStyle: 'bold',
              } as DrawTextCommand,
              true
            );
            y = this.drawText(
              {
                text: prePlanText,
                x: 55,
                y: y,
                width: planWidth - 20,
                fontSize: 12,
                lineSpacing: 6,
              } as DrawTextCommand,
              true
            );
          }

          const color = this.getTripPlanColor(prePlan.type);
          const bgColor = hexToRgb(color.bgColor);
          this.doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
          this.doc.roundedRect(
            48.5,
            this.y,
            planWidth,
            y - this.y - 1,
            4,
            4,
            'F'
          );

          this.y = this.drawText(
            {
              text: `${prePlan.title} · ${prePlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              color: color.textColor,
              fontStyle: 'bold',
            } as DrawTextCommand,
            false
          );

          this.y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: this.y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
              color: '#878787',
            } as DrawTextCommand,
            false
          );

          this.y += 4;
        }
      }

      let description = removeHtmlTags(item.attraction.item.description);
      if (description.length > 800) {
        description = description.slice(0, 800);
        const lastSpaceIndex = description.lastIndexOf(' ');
        if (lastSpaceIndex !== -1) {
          description = description.slice(0, lastSpaceIndex);
        }
        description += '...';
      }

      if (!!description) {
        const yIfDrawText = this.drawText(
          {
            text: description,
            x: 28,
            y: this.y + 5,
            width: boxWidth - 20,
            lineSpacing: 6,
            fontSize: 12,
          } as DrawTextCommand,
          true
        );

        if (yIfDrawText + 50 > contentBottomY) {
          this.addPage(this.command);
        }
      } else {
        if (this.y + 50 > contentBottomY) {
          this.addPage(this.command);
        }
      }

      const boxInitY = this.y;

      this.drawImage(
        ConfigService.config.PDF.PDFMapMarkerYellowImage,
        186,
        this.y,
        8,
        12
      );

      this.drawListPinNumber(index + 1, 186, this.y);

      this.y += 7;
      this.drawItemImage(item, 26, 3);
      const textX = 26 + 40 + 6;
      const textWidth = boxWidth - textX;
      const isTitleOverflow = this.isTextOverflow(
        item.attraction.item.name,
        15,
        textWidth
      );
      const address =
        item.attraction.item.address.address || item.attraction.item.address;
      const isAddressOverflow = this.isTextOverflow(
        address.toString(),
        13,
        textWidth
      );
      if (isTitleOverflow && isAddressOverflow) {
        this.y -= 2.5;
      } else if (!isTitleOverflow && !isAddressOverflow) {
        this.y += 2.5;
      }
      this.drawText({
        text: item.attraction.item.name,
        x: textX,
        y: this.y + 6,
        maxLines: 2,
        width: textWidth + 4,
        fontSize: 15,
        fontStyle: 'bold',
        lineSpacing: 6,
      } as DrawTextCommand);

      if (item.attraction.item['isExpired']) {
        const y = this.y + (isTitleOverflow ? 19 : 13);
        const color = hexToRgb('#fd7c7c');
        this.doc.setFillColor(color.r, color.g, color.b);
        this.doc.roundedRect(textX, y - 4.5, 13, 5, 1, 1, 'F');
        this.drawText({
          text: 'Expired',
          x: textX + 2,
          y: y - 1,
          width: textWidth,
          fontSize: 8,
          color: '#fff',
        } as DrawTextCommand);
      }

      this.drawText({
        text:
          item.attraction.item['timeOneLine'] ||
          item.attraction.item['categoryText'],
        x: textX + (item.attraction.item['isExpired'] ? 14 : 0),
        y: this.y + (isTitleOverflow ? 19 : 13),
        maxLines: 1,
        width: textWidth,
      } as DrawTextCommand);

      this.y = this.drawText({
        text: address,
        x: textX,
        y: this.y + (isTitleOverflow ? 26 : 20),
        maxLines: 2,
        width: textWidth,
        lineSpacing: 6,
      } as DrawTextCommand);

      if (!!description) {
        this.y = this.drawText({
          text: description,
          x: 28,
          y: this.y + 5,
          width: boxWidth - 20,
          lineSpacing: 6,
          fontSize: 12,
        } as DrawTextCommand);
      } else {
        this.y += 3;
      }

      // Draw attraction border
      this.doc.setLineWidth(0.1);
      let color = hexToRgb('#bfbfbf');
      this.doc.setDrawColor(color.r, color.g, color.b);
      this.doc.roundedRect(
        20,
        boxInitY + 3,
        boxWidth,
        this.y - boxInitY - 2,
        3,
        3,
        'S'
      );

      // Draw post plans
      if ((item.attraction as TripAttraction).postPlans?.length > 0) {
        const postPlans = (item.attraction as TripAttraction).postPlans;
        for (const postPlan of postPlans) {
          if (this.y > 230) {
            this.addPage(this.command);
          }

          const prePlanText = removeHtmlTags(postPlan.content);
          let y = this.drawText(
            {
              text: `${postPlan.title} · ${postPlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              fontStyle: 'bold',
            } as DrawTextCommand,
            true
          );

          y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
            } as DrawTextCommand,
            true
          );

          if (y + 4 > contentBottomY) {
            this.addPage(this.command);
            y = this.drawText(
              {
                text: `${postPlan.title} · ${postPlan.time || ''}`,
                x: 55,
                y: this.y + 8,
                width: planWidth - 20,
                fontSize: 13,
                lineSpacing: 6,
                fontStyle: 'bold',
              } as DrawTextCommand,
              true
            );
            y = this.drawText(
              {
                text: prePlanText,
                x: 55,
                y: y,
                width: planWidth - 20,
                fontSize: 12,
                lineSpacing: 6,
              } as DrawTextCommand,
              true
            );
          }

          const color = this.getTripPlanColor(postPlan.type);
          const bgColor = hexToRgb(color.bgColor);
          this.doc.setFillColor(bgColor.r, bgColor.g, bgColor.b);
          this.doc.roundedRect(
            48.5,
            this.y,
            planWidth,
            y - this.y - 1,
            4,
            4,
            'F'
          );

          this.y = this.drawText(
            {
              text: `${postPlan.title} · ${postPlan.time || ''}`,
              x: 55,
              y: this.y + 8,
              width: planWidth - 20,
              fontSize: 13,
              lineSpacing: 6,
              color: color.textColor,
              fontStyle: 'bold',
            } as DrawTextCommand,
            false
          );

          this.y = this.drawText(
            {
              text: prePlanText,
              x: 55,
              y: this.y,
              width: planWidth - 20,
              fontSize: 12,
              lineSpacing: 6,
              color: '#878787',
            } as DrawTextCommand,
            false
          );

          this.y += 4;
        }
      }

      index++;
      this.y += 9;
    }
  }

  private getTripPlanColor(type: string) {
    return TripPlannerService.planTypes.find((x) => x.type === type);
  }

  private drawItineraryAttractions(
    command: ExportItineraryPDFCommand,
    directionData: DirectionData
  ) {
    let index = 0;
    for (const item of this.pdfImageBase64) {
      if (!item.base64) continue;

      const boxWidth = 170;

      const preDesc = removeHtmlTags(
        (item.attraction as ItineraryAttraction).preDescription
      );
      if (!!preDesc) {
        const yIfDrawText = this.drawText(
          {
            text: preDesc,
            x: 28,
            y: this.y + 16,
            width: boxWidth - 20,
            fontSize: 12,
            lineSpacing: 6,
          } as DrawTextCommand,
          true
        );

        if (yIfDrawText > 287) {
          this.addPage(this.command);
        }

        this.y = this.drawText({
          text: preDesc,
          x: 28,
          y: this.y + 16,
          width: boxWidth - 20,
          fontSize: 12,
          lineSpacing: 6,
        } as DrawTextCommand);
      }

      let description = removeHtmlTags(item.attraction.item.description);
      if (description.length > 800) {
        description = description.slice(0, 800);
        const lastSpaceIndex = description.lastIndexOf(' ');
        if (lastSpaceIndex !== -1) {
          description = description.slice(0, lastSpaceIndex);
        }
        description += '...';
      }

      if (!!description) {
        const yIfDrawText = this.drawText(
          {
            text: description,
            x: 28,
            y: this.y + 5,
            width: boxWidth - 20,
            lineSpacing: 6,
            fontSize: 12,
          } as DrawTextCommand,
          true
        );

        if (yIfDrawText + 50 > 287) {
          this.addPage(this.command);
        }
      } else {
        if (this.y + 50 > 287) {
          this.addPage(this.command);
        }
      }

      const boxInitY = this.y;

      if (item.mapIconBase64) {
        this.drawImage(item.mapIconBase64, 183, this.y - 1, 14, 14);
      } else {
        this.drawImage(
          ConfigService.config.PDF.PDFMapMarkerYellowImage,
          186,
          this.y,
          8,
          12
        );
        this.drawListPinNumber(index + 1, 186, this.y);
      }

      this.y += 7;
      this.drawItemImage(item, 26, 3);
      const textX = 26 + 40 + 6;
      const textWidth = boxWidth - textX;
      const isTitleOverflow = this.isTextOverflow(
        item.attraction.item.name,
        15,
        textWidth
      );
      const address =
        item.attraction.item.address.address || item.attraction.item.address;
      const isAddressOverflow = this.isTextOverflow(
        address.toString(),
        13,
        textWidth
      );
      if (isTitleOverflow && isAddressOverflow) {
        this.y -= 2.5;
      } else if (!isTitleOverflow && !isAddressOverflow) {
        this.y += 2.5;
      }
      this.drawText({
        text: item.attraction.item.name,
        x: textX,
        y: this.y + 6,
        maxLines: 2,
        width: textWidth + 4,
        fontSize: 15,
        fontStyle: 'bold',
        lineSpacing: 6,
      } as DrawTextCommand);
      this.drawText({
        text:
          item.attraction.item['timeOneLine'] ||
          item.attraction.item['categoryText'],
        x: textX,
        y: this.y + (isTitleOverflow ? 19 : 13),
        maxLines: 1,
        width: textWidth,
      } as DrawTextCommand);
      this.y = this.drawText({
        text: address,
        x: textX,
        y: this.y + (isTitleOverflow ? 26 : 20),
        maxLines: 2,
        width: textWidth,
        lineSpacing: 6,
      } as DrawTextCommand);
      if (!!description) {
        this.y = this.drawText({
          text: description,
          x: 28,
          y: this.y + 5,
          width: boxWidth - 20,
          lineSpacing: 6,
          fontSize: 12,
        } as DrawTextCommand);
      } else {
        this.y += 3;
      }

      this.doc.setLineWidth(0.1);
      let color = hexToRgb('#bfbfbf');
      this.doc.setDrawColor(color.r, color.g, color.b);
      this.doc.roundedRect(
        20,
        boxInitY + 3,
        boxWidth,
        this.y - boxInitY - 2,
        3,
        3,
        'S'
      );

      const postDesc = removeHtmlTags(
        (item.attraction as ItineraryAttraction).postDescription
      );
      if (!!postDesc) {
        const yIfDrawText = this.drawText(
          {
            text: postDesc,
            x: 28,
            y: this.y + 13,
            width: boxWidth - 20,
            fontSize: 12,
            lineSpacing: 6,
          } as DrawTextCommand,
          true
        );

        if (yIfDrawText > 287) {
          this.addPage(this.command);
        }

        this.y = this.drawText({
          text: postDesc,
          x: 28,
          y: this.y + 13,
          width: boxWidth - 20,
          fontSize: 12,
          lineSpacing: 6,
        } as DrawTextCommand);
      }

      this.y += 15;

      let totalDistanceText = '';
      if (!directionData?.legs) {
        totalDistanceText = `-- to --`;
      } else {
        if (index < directionData.legs.length) {
          const totalDistanceKM =
            Math.round(directionData.legs[index].distance.value / 1000) + ' km';
          totalDistanceText = `-- ${directionData.legs[index].distance.text} / ${totalDistanceKM} to --`;
        }
      }
      const totalDistanceTextWidth = this.getTextWidth(totalDistanceText, 13);
      const offsetX = (210 - totalDistanceTextWidth) / 2;

      if (index !== this.pdfImageBase64.length - 1) {
        this.addNewPageIfNeeded(command, 250);
        this.y = this.drawText({
          x: offsetX,
          y: this.y,
          text: totalDistanceText,
        } as DrawTextCommand);
        this.y += 3;
        this.addNewPageIfNeeded(command, 250);
      }

      index++;
    }
  }

  private async drawDirectionMap(
    trip: Itinerary | TripDay,
    appKey: string
  ): Promise<DirectionData> {
    const directionData = await this.getGoogleMapImageUrlAndLegs(
      trip.attractions,
      appKey
    );
    if (!directionData) return null;

    const mapX = 25;
    const mapWidth = 160;
    const mapImageHeight = 76;
    const outerFrameX = 23.5;
    const outerFrameWidth = 163;
    const outerFrameHeight = 77.4;
    const mapTopY = this.y;

    let base64 = await this.loadImageBase64(
      encodeURIComponent(directionData.mapImageUrl)
    );
    if (base64 && directionData.mapProjection) {
      base64 = await this.compositeMapIcons(
        base64,
        trip.attractions,
        directionData.mapProjection
      );
    }
    base64 &&
      (this.y = this.drawImage(base64, mapX, this.y, mapWidth, mapImageHeight));

    this.doc.setLineWidth(3);
    this.doc.setDrawColor(255, 255, 255);
    this.doc.roundedRect(outerFrameX, mapTopY, outerFrameWidth, outerFrameHeight, 4, 4, 'S');
    let color = hexToRgb('#898989');
    this.doc.setDrawColor(color.r, color.g, color.b);
    this.doc.setLineWidth(0.1);
    this.doc.roundedRect(mapX, mapTopY + 1.3, mapWidth, 75, 3, 3, 'S');

    // Keep cursor aligned with the real drawn container bottom to prevent
    // following blocks from overlapping the map border.
    this.y = Math.max(this.y, mapTopY + outerFrameHeight);

    return directionData;
  }

  // Draws every stop marker onto the static map image, positioned via the
  // deterministic projection. The Static Maps API can't render them itself:
  // labels are limited to a single character (DXP-2100), custom icons over
  // ~64x64 px are silently replaced by the default red pin, and only 5 unique
  // icon URLs are allowed per request. Numbered pins use the same asset as the
  // attraction list below the map so both share one design.
  private async compositeMapIcons(
    mapBase64: string,
    attractions: ItineraryAttraction[] | TripAttraction[],
    projection: StaticMapProjection
  ): Promise<string> {
    try {
      const mapImg = await this.loadHtmlImage(mapBase64);
      const canvas = document.createElement('canvas');
      canvas.width = projection.width;
      canvas.height = projection.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(mapImg, 0, 0, canvas.width, canvas.height);

      const pinImg = await this.loadHtmlImage(
        ConfigService.config.PDF.PDFMapMarkerYellowImage
      );

      const list = attractions as ItineraryAttraction[];
      // Drawn last-to-first so lower-numbered stops stay on top where pins overlap.
      for (let i = list.length - 1; i >= 0; i--) {
        const attraction = list[i];
        if (!attraction.item) {
          continue;
        }
        const lat = attraction.item.address?.lat || attraction.item.lat;
        const lng = attraction.item.address?.lng || attraction.item.lng;
        if (typeof lat !== 'number' || typeof lng !== 'number') {
          continue;
        }

        const point = this.latLngToStaticMapPixel(lat, lng, projection);
        const mapIcon = attraction.mapIcon;

        if (mapIcon) {
          let iconImg: HTMLImageElement;
          try {
            const iconBase64 = await this.loadImageBase64(mapIcon);
            if (!iconBase64) {
              continue;
            }
            iconImg = await this.loadHtmlImage(iconBase64);
          } catch (e) {
            console.error(`Failed to load custom map icon ${mapIcon}:`, e);
            continue;
          }

          // Same sizing rule as the live itinerary map: preset icons at 40x55,
          // uploaded icons at natural size capped so they don't cover the map.
          let iconWidth: number;
          let iconHeight: number;
          if (mapIcon.includes('iti-images.s3.amazonaws.com/imgs/map-icon')) {
            iconWidth = 40;
            iconHeight = 55;
          } else {
            const scale = Math.min(1, 64 / Math.max(iconImg.width, iconImg.height, 1));
            iconWidth = iconImg.width * scale;
            iconHeight = iconImg.height * scale;
          }

          // Bottom-center anchor, matching Google's default marker anchoring.
          ctx.drawImage(
            iconImg,
            point.x - iconWidth / 2,
            point.y - iconHeight,
            iconWidth,
            iconHeight
          );
        } else {
          this.drawNumberedMapPin(ctx, pinImg, point.x, point.y, i + 1);
        }
      }

      return canvas.toDataURL('image/png');
    } catch (e) {
      console.error('Failed to composite custom map icons:', e);
      return mapBase64;
    }
  }

  // Centres the stop number in the white circle of the 8x12 mm list pin drawn
  // at (pinX, pinY), shrinking the font so multi-digit numbers still fit.
  private drawListPinNumber(stopNumber: number, pinX: number, pinY: number) {
    const label = stopNumber.toString();
    const maxTextWidth = 8 * (46 / 70) * 0.85;
    this.doc.setFont(undefined, 'normal');
    let fontSize = 13;
    while (fontSize > 7 && this.getTextWidth(label, fontSize) > maxTextWidth) {
      fontSize--;
    }
    const capHeight = (fontSize * 0.72) / this.doc.internal.scaleFactor;
    this.drawText({
      x: pinX + 4 - this.getTextWidth(label, fontSize) / 2,
      y: pinY + 12 * (35 / 93) + capHeight / 2,
      text: label,
      fontSize,
    } as DrawTextCommand);
  }

  // PDFMapMarkerYellowImage is 70x93 px with its tip at (35, 92) and a white
  // circle of 46 px diameter centred at (35, 35).
  private drawNumberedMapPin(
    ctx: CanvasRenderingContext2D,
    pinImg: HTMLImageElement,
    tipX: number,
    tipY: number,
    stopNumber: number
  ) {
    const scale = MAP_PIN_HEIGHT_PX / 93;
    ctx.drawImage(
      pinImg,
      tipX - 35 * scale,
      tipY - 92 * scale,
      70 * scale,
      93 * scale
    );

    const circleDiameter = 46 * scale;
    const label = stopNumber.toString();
    let fontSize = Math.round(circleDiameter * 0.62);
    ctx.font = `bold ${fontSize}px Helvetica, Arial, sans-serif`;
    while (fontSize > 7 && ctx.measureText(label).width > circleDiameter * 0.8) {
      fontSize--;
      ctx.font = `bold ${fontSize}px Helvetica, Arial, sans-serif`;
    }
    ctx.fillStyle = this.textDefaultColor || '#404040';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, tipX, tipY - 57 * scale);
  }

  private loadHtmlImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  private drawTripInfo(
    trip: Itinerary | TripDay,
    directionData: DirectionData
  ) {
    if (!trip.attractions || trip.attractions.length <= 1) {
      // Keep vertical rhythm consistent with multi-stop layout where the
      // summary row (stops/time/distance) occupies this space.
      this.y += 20;
      return;
    }

    let totalDistance = trip['distance'] || 0,
      totalTravelTime = trip['travelTime'] || 0;
    if (!trip['travelTime'] || !trip['distance']) {
      if (directionData && directionData.legs) {
        directionData.legs.forEach((leg) => {
          totalDistance += leg.distance.value;
          totalTravelTime += leg.duration.value;
        });
      }
    }

    if (!totalDistance && !totalTravelTime) {
      this.y += 20;
      return;
    }

    const totalTravelTimeText =
      this.timeService.convertToTravelTimeText(totalTravelTime);
    const totalDistanceMiles = Math.round(totalDistance / 1610) + ' miles';
    const totalDistanceKM = Math.round(totalDistance / 1000) + ' km';

    const travelTimeWidth = this.getTextWidth(totalTravelTimeText, 13);
    const distanceWidth = this.getTextWidth(
      `${totalDistanceMiles} / ${totalDistanceKM}`,
      13
    );
    const stopsWidth = this.getTextWidth(
      trip.attractions.length.toString(),
      13
    );
    const iconAndSpacingWidth = 3 + 3 + 6 + 22;
    const tripInfoWidth =
      travelTimeWidth + distanceWidth + stopsWidth + iconAndSpacingWidth;

    const offsetX = (210 - tripInfoWidth) / 2;

    this.drawImage(
      ConfigService.config.PDF.PDFMapMarkerImage,
      offsetX,
      this.y + 3,
      3,
      4
    );
    this.drawText({
      text: trip.attractions.length.toString(),
      x: offsetX + 6,
      y: this.y + 7,
    } as DrawTextCommand);

    this.drawImage(
      ConfigService.config.PDF.PDFTimerImage,
      offsetX + 16,
      this.y + 2,
      6,
      6
    );
    this.drawText({
      text: totalTravelTimeText,
      x: offsetX + 23,
      y: this.y + 7,
    } as DrawTextCommand);

    this.drawImage(
      ConfigService.config.PDF.PDFMileImage,
      offsetX + travelTimeWidth + 30,
      this.y + 3.5,
      3,
      3
    );
    this.drawText({
      text: `${totalDistanceMiles} / ${totalDistanceKM}`,
      x: offsetX + travelTimeWidth + 37,
      y: this.y + 7,
    } as DrawTextCommand);

    this.y += 20;
  }

  private drawTripTitle(tripTitle: string) {
    if (tripTitle) {
      let width = 210,
        offsetX = 0;
      if (this.isTextOverflow(tripTitle, 21)) {
        width = 180;
        offsetX = (210 - width) / 2;
      } else {
        offsetX = this.getCenteredTextX(tripTitle, 210);
      }

      this.y = this.drawText({
        text: tripTitle,
        x: offsetX,
        y: this.y,
        fontSize: 21,
        lineSpacing: 9,
        maxLines: 2,
        width: width,
      } as DrawTextCommand);
    }
  }

  private async loadImage(imgUrl: string, type: PDFImageFor) {
    const base64 = await this.loadImageBase64(
      this.imageService.imageSourceUrl(imgUrl)
    );
    if (!base64) {
      console.log(`%cloading ${type} error, ${imgUrl}`, 'color:orange');
    }
    base64 &&
      this.pdfImageBase64.push(new PDFImgDataObject(type, base64, 0, 0));
  }

  private getRelativeWidth(
    imgWidth: number,
    imgHeight: number,
    targetHeight: number
  ) {
    return (imgWidth * targetHeight) / imgHeight;
  }

  private getRelativeHeight(
    imgWidth: number,
    imgHeight: number,
    targetWidth: number
  ) {
    return (imgHeight * targetWidth) / imgWidth;
  }

  private drawItemImage(item, x: number, borderRadius?: number) {
    const lastY = item.directImageData
      ? this.drawBinaryImage(
          item.directImageData,
          item.directImageFormat,
          x,
          this.y,
          40,
          28,
          borderRadius
        )
      : this.drawImage(item.base64, x, this.y, 40, 28, borderRadius);
    this.maxY = lastY + 6;
  }

  private drawBinaryImage(
    imageData: Uint8Array,
    imageFormat: string,
    x: number,
    y: number,
    width: number,
    height: number,
    borderRadius?: number
  ): number {
    try {
      this.doc.addImage(imageData, imageFormat, x, y, width, height);
    } catch (error) {
      console.warn('[ExportPDFService] Direct image drawing failed', error);
      this.doc.addImage(this.errorFallbackImage, 'PNG', x, y, width, height);
    }

    if (borderRadius) {
      this.doc.setLineWidth(3);
      this.doc.setDrawColor(255, 255, 255);
      this.doc.roundedRect(x, y, width, height, borderRadius, borderRadius, 'S');
    }

    return y + height;
  }

  private drawName(item, x: number) {
    const rowY = this.drawText({
      text: item.item.name,
      x,
      y: this.y,
      fontSize: 8,
      fontStyle: 'bold',
      lineSpacing: 4,
      maxLines: 3,
      width: 40,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawTime(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFTimerImage,
      x - 1,
      this.y - 2,
      6,
      6
    );
    const rowY = this.drawText({
      text: item.item.startTimeFormatted,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 1,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawOrganizer(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFOwnerImage,
      x + 0.5,
      this.y - 1,
      3,
      3
    );
    const rowY = this.drawText({
      text: item.item.owner.name,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 2,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawAddress(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFMapMarkerImage,
      x + 0.5,
      this.y - 1.5,
      3,
      4
    );
    const rowY = this.drawText({
      text: item.item['isOnlineEvent']
        ? '(Online event)'
        : item.item.address.address || item.item.address,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 3,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawRating(item, x: number) {
    if (!item.item['rating']) {
      // in order not to have an empty row
      item.item['rating'] = 5;
    }

    this.drawImage(
      ConfigService.config.PDF.PDFStarImage,
      x,
      this.y - 1,
      4,
      3.5
    );
    const rowY = this.drawText({
      text: item.item['rating'].toString(),
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 3,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawCategory(item, x: number) {
    this.drawImage(
      ConfigService.config.PDF.PDFCategoryImage,
      x + 0.5,
      this.y - 0.5,
      3,
      3
    );
    const category = this.processCategory(item.item.categoryList);
    const rowY = this.drawText({
      text: category,
      x: x + 6,
      y: this.y + 2,
      fontSize: 8,
      fontStyle: 'normal',
      lineSpacing: 4,
      maxLines: 3,
      width: 34,
    } as DrawTextCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private processCategory(categoryList: string[]) {
    const newCategoryList = categoryList.map((y) => {
      let temp = y
        .replace(/(_)+/g, ' ')
        .split(' ')
        .map((z) => (z === 'of' ? z : upperFirstLetter(z)));
      return temp.join(' ');
    });
    return newCategoryList.join(', ');
  }

  private drawMoreInfoBtn(item, x: number) {
    const rowY = this.drawFlatButton({
      btnText: 'MORE INFORMATION',
      x,
      y: this.y,
      width: 40,
      height: 8.5,
      link: item.item['eventLink'] || item.item['hostLink'],
      isTextAtPageCenter: false,
    } as DrawFlatLinkButtonCommand);
    this.maxY = Math.max(this.maxY, rowY);
  }

  private drawImage(
    imgBase64: string,
    x: number,
    y: number,
    width: number,
    height: number,
    borderRadius?: number
  ): number {
    try {
      const imageFormat = this.getImageFormat(imgBase64);
      this.doc.addImage(imgBase64, imageFormat, x, y, width, height);
    } catch (e) {
      console.log(
        `%cDraw image error`,
        'color:rgba(133,108,217,1);font-size:30px'
      );
      this.doc.addImage(this.errorFallbackImage, 'PNG', x, y, width, height);
    }

    if (borderRadius) {
      this.doc.setLineWidth(3);
      this.doc.setDrawColor(255, 255, 255);
      this.doc.roundedRect(
        x,
        y,
        width,
        height,
        borderRadius,
        borderRadius,
        'S'
      );
    }

    return y + height;
  }

  private drawText(command: DrawTextCommand, isOnlyGetY = false): number {
    this.doc.setFontSize(command.fontSize || 13);
    this.doc.setFont(undefined, command.fontStyle || 'normal');
    const rgb = hexToRgb(command.color || this.textDefaultColor) ?? { r: 64, g: 64, b: 64 }; // #404040
    this.doc.setTextColor(rgb.r, rgb.g, rgb.b);

    if (isOnlyGetY) {
      return this.splitAndPrintText(
        command.text,
        command.width,
        command.maxLines,
        command.x,
        command.y,
        command.lineSpacing || 4,
        true
      );
    }
    return this.splitAndPrintText(
      command.text,
      command.width,
      command.maxLines,
      command.x,
      command.y,
      command.lineSpacing || 4,
      false
    );
  }

  private addPage(command: ExportPDFCommandBase) {
    this.doc.addPage();
    this.drawFooter(command);
    this.pageNumber++;
    this.drawHeaderBannerRect(command.mainColor, 12, this.pageNumber);
    if (command.type === PDFType.event || command.type === PDFType.place) {
      this.drawEventListPdfPageBackground(12);
    }
    this.y = 26;
  }

  private drawFooter(command: ExportPDFCommandBase) {
    if (command.type === PDFType.event || command.type === PDFType.place) {
      this.drawEventPlaceFooter(command as ExportEventPlacePDFCommand);
      return;
    }

    this.drawTripFooter(command as ExportItineraryPDFCommand);
  }

  private drawEventPlaceFooter(command: ExportEventPlacePDFCommand) {
    const link = this.resolveEventPlaceFooterLink(command);
    const title = command.type === PDFType.place ? 'MORE PLACES' : 'MORE EVENTS';
    const footerHeight = 14;
    const footerY = 297 - footerHeight;
    const maxLinkWidth = 202;
    const displayLink = this.getEventPlaceFooterDisplayLink(link);
    let linkFontSize = 8.3;

    while (linkFontSize > 6.4 && this.getTextWidth(displayLink, linkFontSize) > maxLinkWidth) {
      linkFontSize -= 0.2;
    }

    const color = hexToRgb(command.mainColor || '#5cbcff');
    this.doc.setFillColor(color.r, color.g, color.b);
    this.doc.rect(0, footerY, 210, footerHeight, 'F');
    this.doc.setTextColor(255, 255, 255);

    this.doc.setFont(undefined, 'bold');
    this.doc.setFontSize(11.5);
    this.doc.text(title, this.getCenteredTextX(title), footerY + 5.6);

    this.doc.setFont(undefined, 'normal');
    this.doc.setFontSize(linkFontSize);
    const linkX = (210 - this.getTextWidth(displayLink, linkFontSize)) / 2;
    const linkY = footerY + 10.6;
    this.doc.text(displayLink, linkX, linkY);
    this.doc.link(linkX, linkY - 3.1, this.getTextWidth(displayLink, linkFontSize), 4, { url: link });
  }

  private resolveEventPlaceFooterLink(command: ExportEventPlacePDFCommand) {
    const rawLink =
      command.type === PDFType.place
        ? command.placeCalendarURL
        : command.eventCalendarURL;

    if (!rawLink) {
      return window.location.href;
    }

    return rawLink.startsWith('http') ? rawLink : `https://${rawLink}`;
  }

  private getEventPlaceFooterDisplayLink(link: string) {
    if (!link) return '';

    return link.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }

  private drawTripFooter(command: ExportPDFCommandBase) {
    let tourLink =
      command['moreToursURL'] ||
      `${ConfigService.config.domainV3}${command.hubName}/itineraries`;
    let text =
      command.type === PDFType.tour
        ? `VISIT THIS ITINERARY HERE`
        : `VISIT THIS TRIP HERE`;
    text = text.length > 90 ? text.substring(0, 87) + '...' : text;
    const height = 16;
    this.drawFlatButton({
      btnText: text,
      link: tourLink,
      height,
      x: 0,
      y: 297 - height,
      fontSize: 13,
      width: 210,
      btnColor: command.mainColor,
      isTextAtPageCenter: true,
    } as DrawFlatLinkButtonCommand);
  }

  private drawFlatButton(command: DrawFlatLinkButtonCommand): number {
    const color = hexToRgb(command.btnColor) ?? { r: 92, g: 188, b: 255 }; // #5cbcff
    this.doc.setFillColor(color.r, color.g, color.b);
    this.doc.rect(command.x, command.y, command.width, command.height, 'F');
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(command.fontSize || 8);
    this.doc.setFont(undefined, command.fontStyle || 'bold');
    const offsetX = command.isTextAtPageCenter
      ? this.getCenteredTextX(command.btnText)
      : command.x + 5;
    const offsetY = command.height > 12 ? command.y + 9 : command.y + 5;
    this.doc.textWithLink(command.btnText, offsetX, offsetY, {
      url: command.link,
    });
    return command.y + 5;
  }

  private savePDF(command: ExportPDFCommandBase) {
    const fallbackName =
      command.type === PDFType.event
        ? 'Events'
        : command.type === PDFType.place
          ? 'Places'
          : command.type === PDFType.tour
            ? 'Itineraries'
            : command.type === PDFType.calendarMonth
              ? 'Calendar'
              : 'Trips';
    const title = command.title?.toString() || '';
    const fileName = this.sanitizeFileName(title || fallbackName, fallbackName);

    this.doc.save(`${fileName}.pdf`);
    this.pdfImageBase64 = [];
    command.event && command.event.complete && command.event.complete();
    command.completeCallback && command.completeCallback();
  }

  private sanitizeFileName(rawName: string, fallbackName: string): string {
    let name = (rawName || '').toString().trim();

    // Avoid double extensions like "name.pdf.pdf"
    name = name.replace(/\.pdf$/i, '');
    // Remove control chars and invalid filename characters (Windows-safe)
    name = name.replace(/[\x00-\x1f\x7f]/g, '');
    name = name.replace(/[\\/:*?"<>|]/g, ' ');
    // Normalize whitespace and trim leading/trailing spaces or dots
    name = name.replace(/\s+/g, ' ').trim();
    name = name.replace(/^[. ]+|[. ]+$/g, '');

    if (!name) {
      name = fallbackName;
    }

    // Windows reserved names
    if (/^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i.test(name)) {
      name = `${name}_`;
    }

    const maxLength = 120;
    if (name.length > maxLength) {
      name = name.slice(0, maxLength).trim().replace(/[. ]+$/g, '');
    }

    return name || fallbackName;
  }

  private isTextOverflow(
    text: string,
    fontSize?: number,
    parentWidth?: number
  ) {
    fontSize && this.doc.setFontSize(fontSize);
    const textWidth = this.getTextWidth(text);
    const pageWidth =
      parentWidth ||
      (this.doc.internal.pageSize.width
        ? this.doc.internal.pageSize.width
        : 210);

    return textWidth > pageWidth;
  }

  private getCenteredTextX(text: string, parentWidth?: number) {
    const textWidth = this.getTextWidth(text);
    let pageWidth =
      parentWidth ||
      (this.doc.internal.pageSize.width
        ? this.doc.internal.pageSize.width
        : 210);
    let textOffset = (pageWidth - textWidth) / 2;
    return textOffset;
  }

  private getTextWidth(text: string, fontSize?: number) {
    fontSize && this.doc.setFontSize(fontSize);
    return (
      (this.doc.getStringUnitWidth(text) * this.doc.internal.getFontSize()) /
      this.doc.internal.scaleFactor
    );
  }

  async loadImageBase64(imgUrl: string): Promise<string> {
    if (imgUrl.startsWith('data:')) {
      return imgUrl;
    }

    if (imgUrl.indexOf(ConfigService.config.domainV2) >= 0) {
      // Use more aggressive compression for PDF exports: maxWidth 500px, quality 0.5 (50%)
      const base64 = await this.imageService.getBase64ImageByUrl(imgUrl, 500, 0.5);
      return base64 ? base64.base64 : null;
    }

    const base64 = await this.getBase64FromServer(imgUrl).catch(async (e) => {
      throw e;
    });
    return base64;
  }

  /**
   * Experimental path for trip item images. Set
   * localStorage['imgoing.pdf.directS3Images'] to 'false' to use the original
   * /common/image-base64 path exclusively.
   */
  private async loadDirectS3ImageForPdf(
    imgUrl: string
  ): Promise<{ data: Uint8Array; format: string } | null> {
    if (!imgUrl || localStorage.getItem('imgoing.pdf.directS3Images') === 'false') {
      return null;
    }

    let url: URL;
    try {
      url = new URL(imgUrl);
    } catch {
      return null;
    }

    const host = url.hostname.toLowerCase();
    const isS3Host =
      url.protocol === 'https:' &&
      (host.endsWith('.s3.amazonaws.com') ||
        /^.+\.s3\.[a-z0-9-]+\.amazonaws\.com$/.test(host));
    if (!isS3Host) {
      return null;
    }

    try {
      const response = await fetch(url.toString(), {
        mode: 'cors',
        credentials: 'omit',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const maxBytes = 15 * 1024 * 1024;
      const contentLength = Number(response.headers.get('content-length') || 0);
      if (contentLength > maxBytes) {
        throw new Error('Image exceeds 15 MB');
      }

      const data = new Uint8Array(await response.arrayBuffer());
      if (!data.length || data.length > maxBytes) {
        throw new Error(data.length ? 'Image exceeds 15 MB' : 'Empty image');
      }

      const format = this.getBinaryImageFormat(data);
      if (!format) {
        throw new Error('Unsupported image format');
      }

      // Make sure the jsPDF build currently loaded by the app supports it.
      this.doc.getImageProperties(data, format);
      return { data, format };
    } catch (error) {
      console.warn(
        '[ExportPDFService] Direct S3 image read failed; using Base64 fallback',
        imgUrl,
        error
      );
      return null;
    }
  }

  private getBinaryImageFormat(data: Uint8Array): string | null {
    if (
      data.length >= 8 &&
      data[0] === 0x89 &&
      data[1] === 0x50 &&
      data[2] === 0x4e &&
      data[3] === 0x47
    ) {
      return 'PNG';
    }
    if (data.length >= 3 && data[0] === 0xff && data[1] === 0xd8 && data[2] === 0xff) {
      return 'JPEG';
    }
    if (
      data.length >= 12 &&
      data[0] === 0x52 &&
      data[1] === 0x49 &&
      data[2] === 0x46 &&
      data[3] === 0x46 &&
      data[8] === 0x57 &&
      data[9] === 0x45 &&
      data[10] === 0x42 &&
      data[11] === 0x50
    ) {
      return 'WEBP';
    }
    return null;
  }

  private getImageFormat(base64: string): string {
    if (!base64) return 'PNG';

    // Check if the base64 string contains PNG data
    if (base64.includes('data:image/png') || base64.includes('iVBORw0KGgo')) {
      return 'PNG';
    }

    // Check if the base64 string contains JPEG data
    if (base64.includes('data:image/jpeg') || base64.includes('/9j/')) {
      return 'JPEG';
    }

    // Default to PNG for transparency support
    return 'PNG';
  }

  private getBase64FromServer(imgUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!imgUrl) {
        resolve(null);
        return;
      }

      const url = this.imageService.imageSourceUrl(imgUrl);
      if (this.imageService.isBase64Image(url)) {
        resolve(url);
        return;
      }

      if (url && url.startsWith('http')) {
        this.visitorService.getImageBase64(url).subscribe({
          next: (data) => {
            resolve(data?.succeed ? data.base64 : null);
          },
          error: () => resolve(null),
        });
      } else {
        reject('please check image url.');
      }
    });
  }

  private splitAndPrintText(
    text: string,
    width: number,
    maxLines: number,
    x: number,
    y: number,
    lineSpacing: number,
    isOnlyGetY = false
  ): number {
    let sections = this.doc.splitTextToSize(
      this.replaceSpecialChar(text, false),
      width || 210
    );
    if (sections.length > maxLines) {
      sections = sections.slice(0, maxLines);
      sections[maxLines - 1] = sections[maxLines - 1] + '...';
    }
    for (let section of sections) {
      !isOnlyGetY && this.doc.text(section, x, y);
      y += lineSpacing;
    }
    return y;
  }

  private replaceSpecialChar(description: string, isPrintVersion: boolean) {
    if (!description) return '';

    if (!isPrintVersion) {
      description = description.replace(/(\n)+|(\r\n)+/g, '');
      description = description.replace(/(<br>)+/g, '');
      description = description.replace(/(<br\/>)+/g, '');
      description = description.replace(/(<p>)+/g, '');
      description = description.replace(/(<\/p>)+/g, '');
    } else {
      description = description.replace(/(\n)+|(\r\n)+/g, '<br><br>');
      description = description.replace(/(<br\/>)+/g, '<br>');
      description = description.replace(/(<p>)+/g, '<br><br>');
      description = description.replace(/(<\/p>)+/g, '<br><br>');
    }

    description = description.replace(/(<i>)+/g, '');
    description = description.replace(/(<\/i>)+/g, '');
    description = description.replace(/(<b>)+/g, '');
    description = description.replace(/(<\/b>)+/g, '');
    description = description.replace(/(<strong>)+/g, '');
    description = description.replace(/(<\/strong>)+/g, '');
    description = description.replace(/(<a.*?>)+/g, '');
    description = description.replace(/(<\/a>)+/g, '');
    description = description.replace(/(&#39;)+/g, "'");
    description = description.replace(/(&quot;)+/g, '"');
    description = description.replace(/(&gt;)+/g, '>');
    description = description.replace(/(&lt;)+/g, '<');

    description = this.getAllowedText(description);

    if (description.startsWith(' ')) {
      description = description.substr(1, description.length - 1);
    }

    return description;
  }

  private getAllowedText(section): string {
    if (!this.isAllSymbolAllowed(section)) {
      console.log(`${section}: contains invalid symbols`);
      let newText = '';
      for (let symbol of section.toString().split('')) {
        if (this.isAllSymbolAllowed(symbol)) {
          newText += symbol;
        }
      }
      console.log(`newText: ${newText}`);
      section = newText;
    }
    return section;
  }

  private isAllSymbolAllowed(str) {
    if (!str) {
      return true;
    }
    return new RegExp(
      '^[-+A-Za-z_0-9~é`!@#$%^&*·()\'’".:,/\\\\;<>{}\\[\\]|=? ]+$'
    ).test(str);
  }

  private getGoogleMapImageUrlAndLegs(
    items: ItineraryAttraction[] | TripAttraction[],
    appKey: string
  ): Promise<DirectionData> {
    appKey = appKey || ConfigService.config.google.appKey;

    return new Promise<any>((resolve, reject) => {
      // All stop markers are composited onto the map image afterwards (see
      // compositeMapIcons), so the URL carries no markers and always gets an
      // explicit center/zoom instead of Google's auto-fit to keep the
      // composited pins aligned with the map.
      const stopsBounds = new MapBounds();
      items.forEach((item) => {
        stopsBounds.extend(
          item.item?.address?.lat || item.item?.lat,
          item.item?.address?.lng || item.item?.lng
        );
      });
      if (!items.length || stopsBounds.north < stopsBounds.south) {
        return resolve(null);
      }

      const baseUrl = `https://maps.googleapis.com/maps/api/staticmap?size=640x310&language=en&maptype=roadmap&key=${appKey}`;
      const withProjection = (url: string, mapProjection: StaticMapProjection) =>
        `${url}&center=${mapProjection.centerLat},${mapProjection.centerLng}&zoom=${mapProjection.zoom}`;

      if (items.length === 1) {
        const mapProjection = new StaticMapProjection(
          stopsBounds.north,
          stopsBounds.east,
          14,
          640,
          310
        );
        return resolve({
          mapImageUrl: withProjection(baseUrl, mapProjection),
          legs: null,
          mapProjection,
        });
      }

      const buildStopsOnlyMapUrl = (): DirectionData => {
        const mapProjection = this.buildStaticMapProjection(stopsBounds, 640, 310);
        return {
          mapImageUrl: withProjection(baseUrl, mapProjection),
          legs: null,
          mapProjection,
        };
      };

      this.googleMapService.getDirection(
        items,
        TravelMode.DRIVING,
        (res) => {
          if (!res.overview_polyline || !res.legs) {
            return resolve(buildStopsOnlyMapUrl());
          }

          let mapImageUrl = ConfigService.config.google.multiRealPhoto.replace(
            '${path}',
            res.overview_polyline
          );
          mapImageUrl = mapImageUrl.replace('${appKey}', appKey);

          const bounds = new MapBounds();
          bounds.extend(stopsBounds.north, stopsBounds.east);
          bounds.extend(stopsBounds.south, stopsBounds.west);
          if (res.bounds) {
            const ne = res.bounds.getNorthEast();
            const sw = res.bounds.getSouthWest();
            bounds.extend(ne.lat(), ne.lng());
            bounds.extend(sw.lat(), sw.lng());
          }
          const mapProjection = this.buildStaticMapProjection(bounds, 640, 310);

          resolve({
            mapImageUrl: withProjection(mapImageUrl, mapProjection),
            legs: res.legs,
            mapProjection,
          });
        },
        (status) => {
          console.log(`Get direction error, status: `, status);
          resolve(buildStopsOnlyMapUrl());
        }
      );
    });
  }

  // Latitude to vertical Web-Mercator world fraction (0 = north pole, 1 = south pole).
  private mercatorY(lat: number): number {
    const sin = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999);
    return 0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI);
  }

  private mercatorYToLat(y: number): number {
    return ((2 * Math.atan(Math.exp((0.5 - y) * 2 * Math.PI)) - Math.PI / 2) * 180) / Math.PI;
  }

  // Replicates what Google's auto-fit would pick, but with a known result:
  // the largest integer zoom at which the bounds fit into ~90% of the image,
  // leaving MAP_PIN_HEIGHT_PX free above the bounds so the northernmost
  // stop's pin isn't clipped by the top edge.
  private buildStaticMapProjection(
    bounds: MapBounds,
    width: number,
    height: number
  ): StaticMapProjection {
    const usableFraction = 0.9;
    const latFraction = Math.max(
      this.mercatorY(bounds.south) - this.mercatorY(bounds.north),
      1e-9
    );
    let lngDiff = bounds.east - bounds.west;
    if (lngDiff < 0) {
      lngDiff += 360;
    }
    const lngFraction = Math.max(lngDiff / 360, 1e-9);

    const usableHeight = Math.max(height * usableFraction - MAP_PIN_HEIGHT_PX, 1);
    const latZoom = Math.log2(usableHeight / 256 / latFraction);
    const lngZoom = Math.log2(((width / 256) * usableFraction) / lngFraction);
    const zoom = Math.min(Math.max(Math.floor(Math.min(latZoom, lngZoom)), 1), 15);

    const worldScale = 256 * Math.pow(2, zoom);
    const centerLat = this.mercatorYToLat(
      (this.mercatorY(bounds.north) + this.mercatorY(bounds.south)) / 2 -
        MAP_PIN_HEIGHT_PX / 2 / worldScale
    );
    const centerLng = bounds.west + lngDiff / 2;

    return new StaticMapProjection(centerLat, centerLng, zoom, width, height);
  }

  private latLngToStaticMapPixel(
    lat: number,
    lng: number,
    projection: StaticMapProjection
  ): { x: number; y: number } {
    const worldScale = 256 * Math.pow(2, projection.zoom);
    const x = projection.width / 2 + ((lng - projection.centerLng) / 360) * worldScale;
    const y =
      projection.height / 2 +
      (this.mercatorY(lat) - this.mercatorY(projection.centerLat)) * worldScale;
    return { x, y };
  }

  // Public debugging method
  public async debugLibraryLoading(): Promise<void> {
    try {
      console.log('🔍 [ExportPDFService] Starting library loading debug...');
      await this.loadPDFLibraries();
      console.log(
        '✅ [ExportPDFService] Library loading debug completed successfully'
      );

      console.log('📊 [ExportPDFService] Final state:', {
        'window.jsPDF': typeof window.jsPDF,
        'window.html2canvas': typeof window.html2canvas,
        librariesLoaded: this.librariesLoaded,
      });
    } catch (error) {
      console.error(
        '❌ [ExportPDFService] Library loading debug failed:',
        error
      );
      throw error;
    }
  }

  // Public method to check if libraries are ready
  public areLibrariesReady(): boolean {
    return this.librariesLoaded && !!window.jsPDF && !!window.html2canvas;
  }

  // Public method to force reload libraries
  public async forceReloadLibraries(): Promise<void> {
    console.log('🔄 [ExportPDFService] Force reloading libraries...');
    this.librariesLoaded = false;
    this.loadingPromise = null;
    this.cleanupExistingScripts();
    await this.loadPDFLibraries();
  }

  /**
   * Get detailed error information and solutions for iframe script blocking
   */
  private getIframeBlockingSolutions(error: any): string {
    const isIframe = window.parent !== window;
    const canInject = this.canInjectScripts();

    let solutions = 'PDF Export failed due to script loading issues.\n\n';

    if (isIframe) {
      solutions += '🚨 DETECTED: Your module is running in an iframe context.\n\n';

      if (!canInject) {
        solutions += '❌ ISSUE: Script injection is blocked in this iframe.\n\n';
        solutions += 'SOLUTIONS:\n';
        solutions += '1. Ask the client to remove sandbox restrictions from the iframe\n';
        solutions += '2. Request the client to add "allow-scripts" to iframe sandbox attribute\n';
        solutions += '3. Ask the client to whitelist your domain in their CSP policy\n';
        solutions += '4. Consider using postMessage to communicate with parent window for PDF generation\n\n';
      } else {
        solutions += '✅ Script injection is allowed, but external scripts may be blocked.\n\n';
        solutions += 'SOLUTIONS:\n';
        solutions += '1. Check if the client\'s CSP allows external scripts from CDNs\n';
        solutions += '2. Ask the client to whitelist these domains:\n';
        solutions += '   - https://cdnjs.cloudflare.com\n';
        solutions += '   - https://unpkg.com\n';
        solutions += '3. Consider bundling PDF libraries with your module\n\n';
      }
    } else {
      solutions += '✅ Running in main window context.\n\n';
      solutions += 'SOLUTIONS:\n';
      solutions += '1. Check your internet connection\n';
      solutions += '2. Try refreshing the page\n';
      solutions += '3. Check browser console for detailed errors\n\n';
    }

    solutions += `Technical Error: ${error.message || 'Unknown error'}`;
    return solutions;
  }

  /**
   * Public method to check if the current environment supports PDF export
   * Useful for clients to diagnose iframe script blocking issues
   */
  public async checkEnvironmentSupport(): Promise<{
    supported: boolean;
    reason?: string;
    diagnostics: {
      isIframe: boolean;
      canInjectScripts: boolean;
      cspRestrictions: string;
      sandboxRestrictions: string;
      librariesLoaded: boolean;
    };
    solutions?: string[];
  }> {
    try {
      // Run diagnostics
      this.logIframeDiagnostics();

      const diagnostics = {
        isIframe: window.parent !== window,
        canInjectScripts: this.canInjectScripts(),
        cspRestrictions: this.checkCSPRestrictions(),
        sandboxRestrictions: this.checkSandboxRestrictions(),
        librariesLoaded: this.librariesLoaded
      };

      // Check if we can potentially load libraries
      if (!diagnostics.canInjectScripts) {
        return {
          supported: false,
          reason: 'Script injection is blocked in this iframe context',
          diagnostics,
          solutions: [
            'Remove sandbox restrictions from the iframe',
            'Add "allow-scripts" to iframe sandbox attribute',
            'Whitelist your domain in CSP policy',
            'Use postMessage to communicate with parent window'
          ]
        };
      }

      // Try to load libraries to test
      try {
        await this.loadPDFLibraries();
        return {
          supported: true,
          diagnostics
        };
      } catch (error) {
        return {
          supported: false,
          reason: `Library loading failed: ${error.message}`,
          diagnostics,
          solutions: [
            'Check internet connection',
            'Whitelist CDN domains in CSP policy',
            'Consider bundling PDF libraries with your module'
          ]
        };
      }
    } catch (error) {
      return {
        supported: false,
        reason: `Environment check failed: ${error.message}`,
        diagnostics: {
          isIframe: window.parent !== window,
          canInjectScripts: false,
          cspRestrictions: 'Unknown',
          sandboxRestrictions: 'Unknown',
          librariesLoaded: false
        }
      };
    }
  }
}

// Existing interfaces and classes
export class PDFImgDataObject {
  constructor(
    public imageType: PDFImageFor,
    public base64: string,
    public width: number,
    public height: number,
    public attraction?: ItineraryAttraction | TripAttraction,
    public mapIconBase64?: string,
    public directImageData?: Uint8Array,
    public directImageFormat?: string
  ) {}
}

export enum PDFImageFor {
  top = 'top',
  item = 'item',
  logo = 'logo',
}

export enum PDFType {
  event = 'event',
  place = 'place',
  tour = 'tour',
  trip = 'trip',
  calendarMonth = 'calendarMonth',
}

export class ExportPDFCommandBase {
  title?: string;
  hubName: string;
  type: PDFType;
  isPrintVersion: boolean;
  pdfIntro?: string;
  topImage?: string;
  logo?: string;
  mainColor?: string;
  appKey?: string;
  event?: ButtonTriggerEvent;
  progressCallback?: (progress: number) => void;
  completeCallback?: () => void;
}

export class ExportEventPlacePDFCommand extends ExportPDFCommandBase {
  items: VisitorEvent[] | VisitorPlace[];
  subtitle?: string;
  eventCalendarURL?: string;
  placeCalendarURL?: string;
  from?: Date;
  to?: Date;
  editorChoiceLabel?: string;
  editorChoiceColor?: string;
  hideEditorChoice?: boolean;
}

export class ExportItineraryPDFCommand extends ExportPDFCommandBase {
  trips: Itinerary[];
  moreToursURL?: string;
  isFor3DMap?: boolean;
}

export class ExportTripPDFCommand extends ExportPDFCommandBase {
  trip: UserTrip;
}

export class ExportCalendarMonthPDFCommand extends ExportPDFCommandBase {
  events: VisitorEvent[];
  monthStart: Date;
  timezoneFormatter?: string;
  subtitle?: string;
  eventCalendarURL?: string;
  editorChoiceLabel?: string;
  editorChoiceColor?: string;
  featuredEventBadgeGradient?: string;
  hideEditorChoice?: boolean;
  activeRange?: {
    from: Date;
    to: Date;
  };
}

export class DrawTextCommand {
  text: string;
  x: number;
  y: number;
  width: number;
  maxLines: number;
  lineSpacing: number;
  color: string;
  fontStyle: string;
  fontSize: number;
}

export type DrawIconAndTextCommand = DrawTextCommand & {
  iconBase64: string;
  iconX: number;
  iconY: number;
  iconWidth: number;
  iconHeight: number;
};

export class DrawFlatLinkButtonCommand {
  btnText: string;
  x: number;
  y: number;
  width: number;
  height: number;
  textColor: string;
  btnColor: string;
  fontStyle: string;
  fontSize: number;
  link: string;
  isTextAtPageCenter: boolean;
}

interface CalendarMonthPdfTextBlock {
  text: string;
  fontSize: number;
  lineSpacing: number;
  color?: string;
  fontStyle?: string;
  marginTop: number;
  fillColor?: string;
  fillMode?: 'badge' | 'fullWidth';
  horizontalPadding?: number;
  verticalPadding?: number;
}

interface CalendarMonthPdfEntryBlockGroup {
  blocks: CalendarMonthPdfTextBlock[];
}

interface CalendarMonthPdfWeekSpan {
  key: string;
  title: string;
  isChoice: boolean;
  sortValue: number;
  startDayIndex: number;
  endDayIndex: number;
}

interface CalendarMonthPdfWeekSpanRow {
  spans: CalendarMonthPdfWeekSpan[];
}

interface CalendarMonthPdfCellState {
  day: CalendarMonthPdfDay;
  entryGroups: CalendarMonthPdfEntryBlockGroup[];
  entryGroupIndex: number;
  showDayNumber: boolean;
  showSpanRows: boolean;
}

interface CalendarMonthPdfPlannedCell {
  drawDayNumber: boolean;
  entryGroups: CalendarMonthPdfEntryBlockGroup[];
  usedHeight: number;
}

export class DirectionData {
  mapImageUrl: string;
  legs: DirectionLeg[];
  // Set when the map URL uses an explicit center/zoom so custom stop icons
  // can be composited onto the downloaded map image.
  mapProjection?: StaticMapProjection;
}

export class StaticMapProjection {
  constructor(
    public centerLat: number,
    public centerLng: number,
    public zoom: number,
    public width: number,
    public height: number
  ) {}
}

export class MapBounds {
  north = -90;
  south = 90;
  east = -180;
  west = 180;

  extend(lat: number, lng: number) {
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return;
    }
    this.north = Math.max(this.north, lat);
    this.south = Math.min(this.south, lat);
    this.east = Math.max(this.east, lng);
    this.west = Math.min(this.west, lng);
  }
}

export class DirectionLeg {
  distance: { text: string; value: number };
  duration: { text: string; value: number };
}
